<template>
  <div class="dashboard-page">
    <div ref="mapContainer" class="map-container"></div>
    <div class="map-overlay-red-50"></div>

    <AppHeader title="Dashboard" />

    <div v-if="loading" class="loading-overlay">
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <div class="loading-text">Loading Metering Points...</div>
      </div>
    </div>

    <div v-if="error" class="error-overlay">
      <div class="error-container">
        <div class="error-icon">⚠️</div>
        <div class="error-title">Failed to Load Data</div>
        <div class="error-message">{{ error }}</div>
        <button class="retry-button" @click="loadMeteringPoints">Retry</button>
      </div>
    </div>

    <div class="widget-rail widget-rail-left">
      <div class="widget">
        <div class="widget-header">
          <span class="widget-title">Dashboard View</span>
          <span class="proto-tag">{{ dashboardModeLabel }}</span>
        </div>
        <div class="map-mode-toggle map-style-toggle">
          <button :class="['map-mode-btn', { active: dashboardMode === 'devices' }]" @click="setDashboardMode('devices')">
            <svg class="map-mode-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="6" cy="7" r="3" fill="currentColor" opacity="0.9"/>
              <circle cx="14" cy="7" r="2.4" fill="currentColor" opacity="0.6"/>
              <circle cx="7" cy="14" r="2.2" fill="currentColor" opacity="0.45"/>
              <circle cx="14" cy="14" r="3" fill="currentColor" opacity="0.75"/>
            </svg>
            <span class="map-mode-label">Devices</span>
          </button>
          <button :class="['map-mode-btn', { active: dashboardMode === 'alarms' }]" @click="setDashboardMode('alarms')">
            <svg class="map-mode-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 3.2L17 16H3L10 3.2Z" fill="currentColor" opacity="0.28"/>
              <path d="M10 7.2V11.3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
              <circle cx="10" cy="14" r="1" fill="currentColor"/>
            </svg>
            <span class="map-mode-label">Notifications</span>
          </button>
        </div>
      </div>

      <template v-if="dashboardMode === 'devices'">
      <div class="widget">
        <div class="widget-header">
          <span class="widget-title">Device Fleet</span>
        </div>

        <div class="widget-kpi">
          <span class="kpi-value">{{ stats.totalDevices.toLocaleString() }}</span>
          <span class="kpi-unit">devices</span>
        </div>

        <div class="fleet-chart">
          <div class="fleet-chart-inner">
            <div class="fleet-grid">
              <div
                  v-for="tick in chartTicks"
                  :key="tick.val"
                  class="fleet-gridline"
                  :style="{ bottom: `${(tick.val / chartMax) * 100}%` }"
              >
                <span class="fleet-grid-label">{{ tick.label }}</span>
              </div>
            </div>

            <div class="fleet-bars">
              <div
                  v-for="protocol in protocols"
                  :key="protocol.key"
                  class="fleet-bar-col"
              >
                <div
                    class="fleet-bar"
                    :style="{
                    height: `${(protocol.count / chartMax) * 100}%`,
                    background: barGradient(protocol.color)
                  }"
                />
                <span class="fleet-bar-label">{{ protocol.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="widget">
        <div class="widget-header">
          <span class="widget-title">Metering Points</span>
        </div>

        <div class="widget-kpi">
          <span class="kpi-value">{{ stats.totalMpids.toLocaleString() }}</span>
          <span class="kpi-unit">total</span>
        </div>

        <div class="widget-content">
          <div class="meta-row">
            <span class="meta-label">Without device</span>
            <span class="meta-value meta-warn">{{ stats.mpidsNoDevice.toLocaleString() }}</span>
          </div>

          <div class="meta-bar-wrap">
            <div
                class="meta-bar meta-bar-fill"
                :style="{
                width: `${((stats.totalMpids - stats.mpidsNoDevice) / (stats.totalMpids || 1)) * 100}%`
              }"
            />
            <div
                class="meta-bar meta-bar-warn"
                :style="{ width: `${(stats.mpidsNoDevice / (stats.totalMpids || 1)) * 100}%` }"
            />
          </div>

          <div class="meta-legend">
            <span class="legend-dot" style="background:#5bb6ff"></span>
            <span class="legend-label">Linked</span>
            <span class="legend-dot" style="background:#ff9d4d; margin-left:12px"></span>
            <span class="legend-label">Unlinked</span>
          </div>
        </div>
      </div>

      <div class="widget">
        <div class="widget-header">
          <span class="widget-title">Map View</span>
          <span class="proto-tag">{{ show3d ? '3D Zones' : 'Clusters' }}</span>
        </div>
        <div class="map-mode-toggle map-style-toggle">
          <button :class="['map-mode-btn', { active: show3d }]" @click="setMapMode(true)">
            <svg class="map-mode-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="10" width="14" height="7" rx="2" fill="currentColor" opacity="0.35"/>
              <rect x="5" y="6" width="10" height="6" rx="1.5" fill="currentColor" opacity="0.6"/>
              <rect x="7" y="3" width="6" height="5" rx="1" fill="currentColor" opacity="0.9"/>
            </svg>
            <span class="map-mode-label">3D Zones</span>
          </button>
          <button :class="['map-mode-btn', { active: !show3d }]" @click="setMapMode(false)">
            <svg class="map-mode-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="6" cy="7" r="3.5" fill="currentColor" opacity="0.9"/>
              <circle cx="14" cy="7" r="2.5" fill="currentColor" opacity="0.6"/>
              <circle cx="6" cy="14" r="2" fill="currentColor" opacity="0.4"/>
              <circle cx="13" cy="13" r="3" fill="currentColor" opacity="0.7"/>
            </svg>
            <span class="map-mode-label">Clusters</span>
          </button>
        </div>
      </div>
      </template>

      <template v-else>
      <div class="widget widget-alert">
        <div class="widget-header">
          <span class="widget-title">Latest Notifications</span>
          <span class="status-badge badge-danger">{{ alarmStats.activeDevices }} active</span>
        </div>

        <div class="widget-kpi">
          <span class="kpi-value kpi-danger">{{ alarmStats.activeDevices.toLocaleString() }}</span>
          <span class="kpi-unit">devices</span>
        </div>

        <div class="widget-content">
          <div class="meta-row">
            <span class="meta-label">Located on map</span>
            <span class="meta-value">{{ alarmLocatedDevices.length.toLocaleString() }}</span>
          </div>
          <div class="meta-row">
            <span class="meta-label">Notification records</span>
            <span class="meta-value">{{ alarmStats.historyEntries.toLocaleString() }}</span>
          </div>
        </div>
      </div>

      <div class="widget">
        <div class="widget-header">
          <span class="widget-title">Notification Type</span>
          <span class="proto-tag">{{ alarmTypeCounts.length }}</span>
        </div>

        <div class="widget-content alarm-type-list">
          <div v-if="alarmLoading" class="alarm-empty">Loading notifications...</div>
          <div v-else-if="!alarmTypeCounts.length" class="alarm-empty">No active notifications</div>
          <template v-else>
            <div
                v-for="alarm in alarmTypeCounts"
                :key="alarm.key"
                :class="['alarm-type-row', { active: selectedAlarmTypeKey === alarm.key }]"
                role="button"
                tabindex="0"
                @click="selectAlarmType(alarm.key)"
                @keydown.enter.prevent="selectAlarmType(alarm.key)"
                @keydown.space.prevent="selectAlarmType(alarm.key)"
            >
              <span class="alarm-type-dot" :style="{ background: alarm.color }"></span>
              <span class="alarm-type-name">{{ alarm.label }}</span>
              <span class="alarm-type-count">{{ alarm.count.toLocaleString() }}</span>
            </div>
          </template>
        </div>
      </div>
      </template>
    </div>

    <div class="widget-rail widget-rail-right">
      <template v-if="dashboardMode === 'devices'">
      <div class="stat-tiles">
        <div class="stat-tile">
          <div class="stat-value">{{ stats.totalDevices.toLocaleString() }}</div>
          <div class="stat-label">Devices</div>
        </div>
        <div class="stat-tile">
          <div class="stat-value">{{ stats.totalMpids.toLocaleString() }}</div>
          <div class="stat-label">MPIDs</div>
        </div>
        <div class="stat-tile">
          <div class="stat-value stat-warn">{{ stats.devicesNoMpid.toLocaleString() }}</div>
          <div class="stat-label">No MPID</div>
        </div>
      </div>

      <div class="widget">
        <div class="widget-header">
          <span class="widget-title">Communication</span>
          <div class="tab-group">
            <button :class="['tab', { active: commTab === '24h' }]" @click="commTab = '24h'">24h</button>
            <button :class="['tab', { active: commTab === '5d' }]" @click="commTab = '5d'">5d</button>
            <button :class="['tab', { active: commTab === '15d' }]" @click="commTab = '15d'">15d</button>
          </div>
        </div>

        <div class="widget-content">
          <div v-for="protocol in protocols" :key="protocol.key" class="comm-row">
            <div class="comm-dot" :style="{ background: protocol.color }"></div>
            <span class="comm-name">{{ protocol.name }}</span>

            <div class="comm-bar-wrap">
              <div
                  class="comm-bar"
                  :style="{
                  width: `${(commData[commTab][protocol.key] / (protocol.count || 1)) * 100}%`,
                  background: protocol.color
                }"
              />
            </div>

            <span class="comm-fraction">
              {{ commData[commTab][protocol.key].toLocaleString() }}
              <span class="comm-total"> / {{ protocol.count.toLocaleString() }}</span>
            </span>
          </div>
        </div>

        <div class="widget-footer">
          <span class="footer-note">Devices with at least 1 reading in period</span>
        </div>
      </div>

      <div class="widget widget-alert">
        <div class="widget-header">
          <span class="widget-title">Devices without MPID</span>
          <span class="status-badge badge-warn">Action needed</span>
        </div>

        <div class="widget-kpi">
          <span class="kpi-value kpi-warn">{{ stats.devicesNoMpid.toLocaleString() }}</span>
          <span class="kpi-unit">unlinked</span>
        </div>

        <div class="widget-content">
          <div class="meta-row">
            <span class="meta-label">% of total fleet</span>
            <span class="meta-value">
              {{
                stats.totalDevices > 0
                    ? ((stats.devicesNoMpid / stats.totalDevices) * 100).toFixed(1)
                    : '0.0'
              }}%
            </span>
          </div>
        </div>
      </div>

      <div
          v-if="protocols.some(p => (p.key === 'dlms' || p.key === 'lora_wbus') && p.count > 0)"
          class="widget"
      >
        <div class="widget-header">
          <span class="widget-title">SIM Network</span>
          <span class="proto-tag">DLMS</span>
        </div>

        <div class="sim-gauge-body">
          <div class="sim-gauge-chart">
            <svg viewBox="0 0 120 120" width="108" height="108">
              <circle
                  v-for="(dot, i) in simDots"
                  :key="i"
                  :cx="dot.x"
                  :cy="dot.y"
                  :r="dot.r"
                  :fill="dot.color"
              />
              <text
                  x="60"
                  y="57"
                  text-anchor="middle"
                  font-size="22"
                  fill="rgba(255,255,255,0.96)"
                  font-weight="700"
                  font-family="Inter, sans-serif"
              >
                {{ simPct }}%
              </text>
              <text
                  x="60"
                  y="70"
                  text-anchor="middle"
                  font-size="9"
                  fill="rgba(255,255,255,0.38)"
                  font-weight="600"
                  font-family="Inter, sans-serif"
                  letter-spacing="0.08em"
              >
                SIM ONLINE
              </text>
            </svg>
          </div>

          <div class="sim-gauge-info">
            <div class="sim-status-row">
              <span class="sim-status-dot" :style="{ background: simColor }"></span>
              <span class="sim-status-text" :style="{ color: simColor }">{{ simStatus }}</span>
            </div>

            <div class="sim-num-group">
              <div class="sim-num-item">
                <span class="sim-num-val">{{ stats.simOnline.toLocaleString() }}</span>
                <span class="sim-num-lbl">online</span>
              </div>

              <div class="sim-num-divider"></div>

              <div class="sim-num-item">
                <span class="sim-num-val" :style="{ color: simColor }">
                  {{ Math.max(0, simDenominator - stats.simOnline).toLocaleString() }}
                </span>
                <span class="sim-num-lbl">offline</span>
              </div>
            </div>

            <p class="sim-gauge-desc">{{ simDesc }}</p>
          </div>
        </div>
      </div>
      </template>

      <template v-else>
      <div class="stat-tiles">
        <div class="stat-tile">
          <div class="stat-value stat-danger">{{ alarmFilteredDevices.length.toLocaleString() }}</div>
          <div class="stat-label">{{ selectedAlarmDay ? 'Selected Day' : 'On Map' }}</div>
        </div>
        <div class="stat-tile">
          <div class="stat-value">{{ alarmDevices.length.toLocaleString() }}</div>
          <div class="stat-label">Tracked</div>
        </div>
      </div>

      <div class="widget">
        <div class="widget-header">
          <span class="widget-title">Notification Selection</span>
          <span class="proto-tag">{{ alarmDayLabel }}</span>
        </div>
        <div class="widget-content">
          <div class="meta-row">
            <span class="meta-label">Current filter</span>
            <span class="meta-value">{{ alarmSelectionLabel }}</span>
          </div>
          <div class="meta-row">
            <span class="meta-label">Map markers</span>
            <span class="meta-value">{{ alarmMarkersClustered ? 'Clustered' : 'Individual' }}</span>
          </div>
          <div class="map-mode-toggle map-style-toggle alarm-selection-toggle">
            <button
                :class="['map-mode-btn', { active: !selectedAlarmDay }]"
                type="button"
                @click="selectAlarmDay(null)"
            >
              <svg class="map-mode-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 5.5H16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                <path d="M4 10H14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" opacity="0.72"/>
                <path d="M4 14.5H11" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" opacity="0.48"/>
              </svg>
              <span class="map-mode-label">Latest notifications</span>
            </button>
            <button
                :class="['map-mode-btn', { active: !alarmMarkersClustered }]"
                type="button"
                @click="toggleAlarmMarkerClustering"
            >
              <svg class="map-mode-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="6" cy="7" r="3.2" fill="currentColor" opacity="0.86"/>
                <circle cx="14" cy="7" r="2.3" fill="currentColor" opacity="0.58"/>
                <circle cx="7" cy="14" r="2" fill="currentColor" opacity="0.44"/>
                <circle cx="14" cy="14" r="2.8" fill="currentColor" opacity="0.72"/>
              </svg>
              <span class="map-mode-label">{{ alarmMarkersClustered ? 'Individual markers' : 'Cluster notifications' }}</span>
            </button>
            <button
                :class="['map-mode-btn alarm-persistent-toggle', { active: showPersistentAlarmsOnly }]"
                type="button"
                @click="togglePersistentAlarmFilter"
            >
              <svg class="map-mode-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 2.8L17.2 16.4H2.8L10 2.8Z" fill="currentColor" opacity="0.20"/>
                <path d="M10 7V11" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
                <circle cx="10" cy="14" r="1" fill="currentColor"/>
              </svg>
              <span class="map-mode-label">Persistent notifications</span>
            </button>
          </div>
        </div>
      </div>

        <div class="widget">
          <div class="widget-header">
            <span class="widget-title">Most Persistent Device Notifications</span>
            <span class="proto-tag">{{ mostPersistentAlarmList.length }}</span>
          </div>

          <div class="widget-content latest-alarm-list">
            <div v-if="alarmLoading" class="alarm-empty">Loading notifications...</div>

            <div v-else-if="!mostPersistentAlarmList.length" class="alarm-empty">
              No persistent notification devices
            </div>

            <template v-else>
              <div
                  v-for="device in mostPersistentAlarmList"
                  :key="device.ldn"
                  class="latest-alarm-row"
              >
                <div>
                  <div>
                    <a
                      v-if="device.ldn"
                      class="latest-alarm-ldn latest-alarm-ldn-link"
                      :href="getDeviceUrl(device.ldn)"
                      target="_blank"
                      rel="noopener noreferrer"
                      :title="`Open ${device.ldn} in new tab`"
                  >
                    <span>{{ device.ldn }}</span>
                    <span class="latest-alarm-link-icon" aria-hidden="true">↗</span>
                  </a>

                    <span v-else class="latest-alarm-ldn">N/A</span>
                  </div>

                  <div class="latest-alarm-meta">
                    {{ device.primaryAlarmLabel }}
                    <template v-if="device.persistentAlarmReason">
                      · {{ device.persistentAlarmReason }}
                    </template>
                  </div>
                </div>

                <span class="latest-alarm-time">
                  {{ device.persistentAlarmLabel }}
                </span>
              </div>
            </template>
          </div>
        </div>
      </template>
    </div>
    <transition name="alarm-history-panel">
      <div v-if="dashboardMode === 'alarms'" class="alarm-history-widget widget">
        <div class="alarm-history-header">
          <div>
            <span class="widget-title">Notification History</span>
            <span class="alarm-history-subtitle">Notification events per UTC day</span>
          </div>
          <button
              :class="['alarm-action-button', { active: !selectedAlarmDay }]"
              type="button"
              @click="selectAlarmDay(null)"
          >
            Latest
          </button>
        </div>

        <div :class="['alarm-history-chart-wrap', { loading: alarmHistoryLoading }]">
          <svg
              class="alarm-history-chart"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              role="img"
              aria-label="Notification history timeline for the last 14 days"
          >
            <defs>
              <linearGradient id="alarmHistoryLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#ff5b6e" />
                <stop offset="55%" stop-color="#ff9d4d" />
                <stop offset="100%" stop-color="#f4c84d" />
              </linearGradient>
              <linearGradient id="alarmHistoryAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="rgba(255, 91, 110, 0.22)" />
                <stop offset="58%" stop-color="rgba(255, 157, 77, 0.09)" />
                <stop offset="100%" stop-color="rgba(255, 91, 110, 0)" />
              </linearGradient>
            </defs>

            <line
                v-for="guide in alarmTimelineGuides"
                :key="guide.key"
                class="alarm-history-guide"
                x1="0"
                x2="100"
                :y1="guide.y"
                :y2="guide.y"
            />
            <path v-if="alarmTimelineAreaPath" class="alarm-history-area" :d="alarmTimelineAreaPath" />
            <path v-if="alarmTimelineLinePath" class="alarm-history-line" :d="alarmTimelineLinePath" />

            <g
                v-for="point in alarmTimelinePoints"
                :key="`alarm-hit-${point.key}`"
                class="alarm-history-hit"
                role="button"
                tabindex="0"
                @click="selectAlarmDay(point.day)"
                @keydown.enter="selectAlarmDay(point.day)"
                @keydown.space.prevent="selectAlarmDay(point.day)"
            >
              <rect
                  class="alarm-history-click-zone"
                  :x="point.xPct - 3.8"
                  y="4"
                  width="7.6"
                  height="84"
                  rx="1.2"
              />
              <line
                  v-if="selectedAlarmDay === point.day"
                  class="alarm-history-selected-line"
                  :x1="point.xPct"
                  :x2="point.xPct"
                  y1="8"
                  y2="80"
              />
            </g>
          </svg>

          <div class="alarm-history-points-layer" aria-hidden="true">
            <span
                v-for="point in alarmTimelinePoints"
                :key="`alarm-dot-${point.key}`"
                :class="['alarm-history-point-dot', { active: point.day === alarmWidgetDayKey }]"
                :style="{ left: `${point.xPct}%`, top: `${point.yPct}%` }"
            />
          </div>

          <div class="alarm-history-label-row">
            <button
                v-for="point in alarmTimelinePoints"
                :key="`alarm-label-${point.key}`"
                :class="['alarm-history-label-button', { active: point.day === alarmWidgetDayKey }]"
                type="button"
                @click="selectAlarmDay(point.day)"
            >
              {{ point.label }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import * as THREE from 'three';
import {
  area,
  booleanPointInPolygon,
  buffer,
  centroid,
  concave,
  convex,
  featureCollection,
  intersect,
  point,
  union
} from '@turf/turf';

import AppHeader from './AppHeader.vue';
import MpidApiService from '@/services/MpidApiService';
import DeviceApiServices from '@/services/DeviceApiServices';
import AlarmApiService from '@/services/AlarmApiService';

const METERS_CACHE = {
  meteringPoints: null,
  meteringPointsAt: 0,
  TTL: 5 * 60 * 1000
};

const PROTOCOL_DEFAULTS = [
  { name: 'DLMS', key: 'dlms', count: 0, color: '#7bb3e0' },
  { name: 'LoRa', key: 'lora', count: 0, color: '#5b7cff' },
  { name: 'WBus', key: 'wbus', count: 0, color: '#5bbfa0' },
  { name: 'CTR', key: 'ctr', count: 0, color: '#5bc8e0' },
  { name: 'wLora', key: 'lora_wbus', count: 0, color: '#e05ba0' }
];

const ALARM_COLORS = {
  tamper: '#ff5b6e',
  metrological: '#c084fc',
  emptyPipe: '#5bb6ff',
  leak: '#ff5b6e',
  burst: '#ff3b4f',
  backflow: '#ff9d4d',
  rf: '#5bc8e0',
  programming: '#8b7cff',
  system: '#ff6b8a',
  temperature: '#f4c84d',
  tilt: '#f59e0b',
  qmax: '#fb7185',
  dry: '#b8f2d2',
  overflow: '#5bb6ff',
  lowBattery: '#c084fc',
  invalid: '#f87171',
  reserved: '#8b95a7',
  generic: '#ff6b8a'
};

const ALARM_DEFINITIONS = [
  { bit: 0, code: 'T', key: 'tamper', label: 'Tamper detection', color: ALARM_COLORS.tamper },
  { bit: 1, code: 'PUS', key: 'pus', label: 'Failure in metrological core', color: ALARM_COLORS.metrological },
  { bit: 2, code: 'BF', key: 'backflow', label: 'Back flow', color: ALARM_COLORS.backflow },
  { bit: 3, code: 'L', key: 'leak', label: 'Leak notification', color: ALARM_COLORS.leak },
  { bit: 4, code: 'RF', key: 'rf', label: 'Poor RF Performance', color: ALARM_COLORS.rf },
  { bit: 5, code: 'LB', key: 'low-battery', label: 'Low battery', color: ALARM_COLORS.lowBattery },
  { bit: 6, code: 'FP', key: 'field-programming', label: 'Field programming detected', color: ALARM_COLORS.programming },
  { bit: 7, code: 'S', key: 'system-alert', label: 'System alert', color: ALARM_COLORS.system },
  { bit: 8, code: '-C\u00B0', key: 'temperature-alert', label: 'Temperature alert', color: ALARM_COLORS.temperature },
  { bit: 9, code: 'TILT', key: 'tilt', label: 'Tilt alert', color: ALARM_COLORS.tilt },
  { bit: 10, code: 'QMAX', key: 'qmax', label: 'Qmax threshold crossed', color: ALARM_COLORS.qmax },
  { bit: 11, code: 'BU', key: 'burst', label: 'Burst', color: ALARM_COLORS.burst },
  { bit: 12, code: 'EP', key: 'empty-pipe', label: 'Empty Pipe', color: ALARM_COLORS.emptyPipe },
  { bit: 13, code: 'SDLB', key: 'slave-low-battery', label: 'Slave device low battery', color: ALARM_COLORS.lowBattery },
  { bit: 14, code: 'INVR', key: 'invalid-read', label: 'Invalid read from slave device', color: ALARM_COLORS.invalid },
  { bit: 15, code: 'RESERVED', key: 'reserved', label: 'Reserved', color: ALARM_COLORS.reserved }
];

const ALARM_BY_CODE = new Map(ALARM_DEFINITIONS.map(alarm => [alarm.code, alarm]));

const DLMS_ALARM_DEFINITIONS = [
  { bit: 0, code: 'CLOCK_SYNCHRONIZATION_FAILURE', key: 'dlms-clock-sync-failure', label: 'Clock synchronization failure', color: ALARM_COLORS.temperature },
  { bit: 1, code: 'METROLOGY_LOG_FULL', key: 'dlms-metrology-log-full', label: 'Metrology log full', color: ALARM_COLORS.metrological },
  { bit: 2, code: 'METROLOGY_LOG_NEARLY_FULL', key: 'dlms-metrology-log-nearly-full', label: 'Metrology log nearly full', color: ALARM_COLORS.metrological },
  { bit: 3, code: 'METROLOGY_ALGORITHM_FAILURE', key: 'dlms-metrology-algorithm-failure', label: 'Metrology algorithm failure', color: ALARM_COLORS.metrological },
  { bit: 4, code: 'SOFTWARE_ERROR', key: 'dlms-software-error', label: 'Software error', color: ALARM_COLORS.system },
  { bit: 5, code: 'FLOW_ERROR', key: 'dlms-flow-error', label: 'Flow error', color: ALARM_COLORS.backflow },
  { bit: 6, code: 'MEMORY_ERROR', key: 'dlms-memory-error', label: 'Memory error', color: ALARM_COLORS.invalid },
  { bit: 7, code: 'DEVICE_STATUS_CHANGED', key: 'dlms-device-status-changed', label: 'Device status changed', color: ALARM_COLORS.rf },
  { bit: 8, code: 'BATTERY_NEARLY_EMPTY', key: 'dlms-battery-nearly-empty', label: 'Battery nearly empty', color: ALARM_COLORS.lowBattery },
  { bit: 9, code: 'BATTERY_AT_CRITICAL_LEVEL', key: 'dlms-battery-critical', label: 'Battery at critical level', color: ALARM_COLORS.lowBattery },
  { bit: 10, code: 'TAMPER_DETECTION', key: 'dlms-tamper-detection', label: 'Tamper detection', color: ALARM_COLORS.tamper },
  { bit: 11, code: 'DST_ACTIVE', key: 'dlms-dst-active', label: 'DST active', color: ALARM_COLORS.programming },
  { bit: 12, code: 'VALVE_PROBLEM_OR_ERROR', key: 'dlms-valve-problem', label: 'Valve problem or error', color: ALARM_COLORS.burst },
  { bit: 13, code: 'CLOCK_SYNCHRONIZATION_PENDING', key: 'dlms-clock-sync-pending', label: 'Clock synchronization pending', color: ALARM_COLORS.temperature },
  { bit: 14, code: 'UNKNOWN', key: 'dlms-unknown', label: 'Unknown', color: ALARM_COLORS.reserved },
  { bit: 15, code: 'SONATA_ALARM', key: 'dlms-sonata-alarm', label: 'Sonata notification', color: ALARM_COLORS.generic }
];

const DLMS_ALARM_BY_CODE = new Map(DLMS_ALARM_DEFINITIONS.map(alarm => [alarm.code, alarm]));

// Maddalena arrives with mapped alarm names. Its raw bits have a different
// layout from WaterTech and must never be decoded with ALARM_DEFINITIONS.
const MADDALENA_ALARM_DEFINITIONS = [
  { code: 'NO_CONSUMPTION', key: 'no-consumption', label: 'No consumption', color: ALARM_COLORS.dry },
  { code: 'METER_REVERSED', key: 'meter-reversed', label: 'Meter reversed', color: ALARM_COLORS.tilt },
  { code: 'SEALING_PERIOD_EXPIRED', key: 'sealing-period-expired', label: 'Expired sealing period', color: ALARM_COLORS.system },
  { code: 'CONFIGURATION_RESET', key: 'configuration-reset', label: 'Configuration reset to default', color: ALARM_COLORS.programming },
  { code: 'METER_BLOCKED', key: 'meter-blocked', label: 'Blocked meter', color: ALARM_COLORS.metrological },
  { code: 'MCU_RESET', key: 'mcu-reset', label: 'MCU reset', color: ALARM_COLORS.system },
  { code: 'RADIO_ERROR', key: 'radio-error', label: 'Radio error', color: ALARM_COLORS.rf },
  { code: 'CHECKSUM_ERROR', key: 'checksum-error', label: 'Checksum error', color: ALARM_COLORS.invalid },
  { code: 'MEASUREMENT_SENSOR_ERROR', key: 'measurement-sensor-error', label: 'Measurement sensor error', color: ALARM_COLORS.metrological }
];
const MADDALENA_ALARM_BY_CODE = new Map([
  ...ALARM_BY_CODE,
  ...DLMS_ALARM_BY_CODE,
  ...MADDALENA_ALARM_DEFINITIONS.map(alarm => [alarm.code, alarm])
]);

const DLMS_DST_ACTIVE_ALARM = DLMS_ALARM_BY_CODE.get('DST_ACTIVE');
const SUPPRESSED_DLMS_ALARM_CODES = new Set(['DST_ACTIVE']);
const SUPPRESSED_DLMS_ALARM_KEYS = new Set(['dlms-dst-active']);
const SUPPRESSED_DLMS_ALARM_LABELS = new Set(['dst active']);

const ALARM_DURATION_FIELDS = [
  { key: 'leakage_duration', lastKey: 'last_leakage_duration', label: 'Leakage' },
  { key: 'pipe_burst_duration', lastKey: 'last_pipe_burst_duration', label: 'Pipe burst' },
  { key: 'backflow_duration', lastKey: 'last_backflow_duration', label: 'Backflow' },
  { key: 'temperature_duration', lastKey: 'last_temperature_duration', label: 'Temperature' },
  { key: 'dry_duration', lastKey: 'last_dry_duration', label: 'Dry' },
  { key: 'overflow_duration', lastKey: 'last_overflow_duration', label: 'Overflow' }
];

const MAP_PADDING = { top: 120, bottom: 120, left: 320, right: 320 };

// ~2.5 km in degrees — merge nearby points into the same hull cluster
const HULL_CLUSTER_EPSILON = 0.025;

const ZONE_STACK_BASE_HEIGHT = 96;
const ZONE_STACK_HEIGHT_STEP = 24;
const ZONE_BOTTOM_EDGE_LIFT_METERS = 0.9;
const ZONE_TOP_CAP_BASE_LIFT_METERS = 2.8;
const ZONE_TOP_CAP_DOT_LIFT_METERS = 5.7;
const ZONE_TOP_CAP_EDGE_LIFT_METERS = 8.8;

// Screen-space pattern tuning for the illuminated slab cap.
// Smaller, denser dots and a rotated / staggered grid make the cap look
// embedded into the surface instead of like vertical overlay columns.
const ZONE_DOT_SCREEN_SPACING_PX = 24;
const ZONE_DOT_SCREEN_RADIUS_PX = 0.58;

// MapLibre ground resolution at zoom 0, lat 0 (for 512px tiles).
// mpp(lat, zoom) = BASE * cos(lat) / 2^zoom
const MAP_BASE_METERS_PER_PX = 78271.517;

export default {
  name: 'DashboardPage',
  components: { AppHeader },

  data() {
    return {
      map: null,
      currentStyle: 'dataviz-dark',
      styles: {
        'dataviz-dark':
            'https://api.maptiler.com/maps/dataviz-dark/style.json?key=4DXCyU0jQcYTU9cFAGQu',
        'streets-v2':
            'https://api.maptiler.com/maps/streets-v2/style.json?key=4DXCyU0jQcYTU9cFAGQu'
      },

      meteringPoints: [],
      loading: false,
      error: null,
      statsLoading: true,
      statsError: null,
      commTab: '24h',
      statsInterval: null,
      zoomWatcher: null,
      threeCapLayer: null,
      mapIsReady: false,
      layerBuildVersion: 0,
      show3d: true,
      dashboardMode: 'devices',

      protocolMarkers: [],
      alarmMarkers: [],
      activeAlarmPopupKey: null,
      suppressAlarmPopupCloseTracking: false,
      alarmClusterMoveWatcher: null,
      alarmDevices: [],
      alarmHistoryBatches: [],
      alarmLoading: false,
      alarmHistoryLoading: false,
      alarmLoaded: false,
      alarmHistoryDaysWindow: 14,
      selectedAlarmDay: null,
      selectedAlarmTypeKey: null,
      showPersistentAlarmsOnly: false,
      alarmMarkersClustered: true,
      alarmDailyOrdered: [],

      stats: {
        totalDevices: 0,
        totalMpids: 0,
        mpidsNoDevice: 0,
        devicesNoMpid: 0,
        simOnline: 0,
        simTotal: 0
      },

      protocols: [...PROTOCOL_DEFAULTS],

      commData: {
        '24h': { dlms: 0, lora: 0, wbus: 0, ctr: 0, lora_wbus: 0 },
        '5d': { dlms: 0, lora: 0, wbus: 0, ctr: 0, lora_wbus: 0 },
        '15d': { dlms: 0, lora: 0, wbus: 0, ctr: 0, lora_wbus: 0 }
      },

      chartMax: 100,
      chartTicks: [{ val: 0, label: '0' }]
    };
  },

  computed: {
    simDenominator() {
      if (this.stats.simTotal > 0) return this.stats.simTotal;

      return (
          this.protocols
              .filter(p => p.key === 'dlms' || p.key === 'lora_wbus')
              .reduce((sum, p) => sum + p.count, 0) || 1
      );
    },

    simPct() {
      return Math.round((this.stats.simOnline / this.simDenominator) * 100);
    },

    simColor() {
      const ratio = this.stats.simOnline / this.simDenominator;
      if (ratio >= 0.95) return '#39d98a';
      if (ratio >= 0.85) return '#63e6be';
      if (ratio >= 0.7) return '#f4c84d';
      if (ratio >= 0.5) return '#ff9d4d';
      return '#ff5b6e';
    },

    simStatus() {
      const ratio = this.stats.simOnline / this.simDenominator;
      if (ratio >= 0.95) return 'Excellent';
      if (ratio >= 0.85) return 'Good';
      if (ratio >= 0.7) return 'Moderate';
      if (ratio >= 0.5) return 'Fair';
      return 'Poor';
    },

    simDesc() {
      const ratio = this.stats.simOnline / this.simDenominator;
      if (ratio >= 0.95) return 'All DLMS devices are communicating normally.';
      if (ratio >= 0.85) return 'Most SIM cards are active. Minor issues detected.';
      if (ratio >= 0.7) return 'Moderate connectivity. Some devices may need attention.';
      if (ratio >= 0.5) return 'Significant SIM outages. Immediate review recommended.';
      return 'Critical SIM failure. Most DLMS devices are unreachable.';
    },

    simDots() {
      const rings = [
        { r: 47, dotR: 2.6 },
        { r: 40, dotR: 1.7 }
      ];

      const count = 30;
      const cx = 60;
      const cy = 60;
      const startDeg = 135;
      const totalDeg = 270;
      const pct = this.stats.simOnline / this.simDenominator;
      const filledCount = Math.round(count * pct);

      const hex = this.simColor;
      const endR = parseInt(hex.slice(1, 3), 16);
      const endG = parseInt(hex.slice(3, 5), 16);
      const endB = parseInt(hex.slice(5, 7), 16);

      const startR = Math.round(endR * 0.12);
      const startG = Math.round(endG * 0.12);
      const startB = Math.round(endB * 0.12);

      const result = [];

      rings.forEach(({ r, dotR }, ringIndex) => {
        for (let i = 0; i < count; i += 1) {
          const deg = startDeg + (totalDeg / (count - 1)) * i;
          const rad = (deg * Math.PI) / 180;
          const filled = i < filledCount;

          let color =
              ringIndex === 0
                  ? 'rgba(255,255,255,0.08)'
                  : 'rgba(255,255,255,0.05)';

          if (filled) {
            const t = filledCount > 1 ? i / (filledCount - 1) : 1;
            const cr = Math.round(startR + (endR - startR) * t);
            const cg = Math.round(startG + (endG - startG) * t);
            const cb = Math.round(startB + (endB - startB) * t);
            const ca = (0.22 + 0.78 * t).toFixed(2);
            color = `rgba(${cr},${cg},${cb},${ca})`;
          }

          result.push({
            x: (cx + r * Math.cos(rad)).toFixed(2),
            y: (cy + r * Math.sin(rad)).toFixed(2),
            r: dotR,
            color
          });
        }
      });

      return result;
    },

    todayKey() {
      const d = new Date();
      return this.dateToUtcDayKey(d);
    },

    dashboardModeLabel() {
      return this.dashboardMode === 'alarms' ? 'notifications' : this.dashboardMode;
    },

    alarmWidgetDayKey() {
      return this.selectedAlarmDay || this.todayKey;
    },

    alarmDayLabel() {
      if (!this.selectedAlarmDay) return 'Latest';
      if (this.selectedAlarmDay === this.todayKey) return 'Today';
      const dt = this.utcDayKeyToDate(this.selectedAlarmDay);
      return dt.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    },

    selectedAlarmType() {
      if (!this.selectedAlarmTypeKey) return null;
      return this.alarmTypeCounts.find(alarm => alarm.key === this.selectedAlarmTypeKey) || null;
    },

    alarmSelectionLabel() {
      const parts = [this.selectedAlarmDay ? 'History day' : 'Latest snapshot'];
      if (this.selectedAlarmType) parts.push(this.selectedAlarmType.label);
      if (this.showPersistentAlarmsOnly) parts.push('Persistent');
      return parts.join(' / ');
    },

    currentAlarmDevices() {
      return this.alarmDevices
          .filter(Boolean)
          .map(device => this.decorateAlarmDevice(device))
          .filter(device => device.hasCurrentAlarm);
    },

    alarmTypeCounts() {
      const counts = new Map();

      for (const device of this.currentAlarmDevices) {
        for (const alarm of device.alarmTypes) {
          const current = counts.get(alarm.key) || { ...alarm, count: 0 };
          current.count += 1;
          counts.set(alarm.key, current);
        }
      }

      return Array.from(counts.values()).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    },

    alarmStats() {
      return {
        activeDevices: this.currentAlarmDevices.length,
        historyEntries: this.alarmHistoryBatches.reduce((sum, batch) => {
          return sum + (Array.isArray(batch?.entries) ? batch.entries.length : 0);
        }, 0)
      };
    },

    alarmLocationIndex() {
      const index = new Map();

      for (const mp of this.meteringPoints) {
        const ldn = this.getMeteringPointDeviceLdn(mp);
        const lat = parseFloat(mp?.lat);
        const lon = parseFloat(mp?.lon);

        if (!ldn || !Number.isFinite(lat) || !Number.isFinite(lon)) continue;
        index.set(ldn.toLowerCase(), {
          lat,
          lon,
          mpid: mp?.mpid || 'N/A',
          customer: mp?.customer_fname || mp?.customerFName || 'N/A',
          street: mp?.street || 'N/A',
          city: mp?.city || 'N/A',
          zipcode: mp?.zip_code || mp?.zipcode || mp?.zipCode || 'N/A'
        });
      }

      return index;
    },

    alarmDevicesForSelectedDay() {
      if (!this.selectedAlarmDay) return [];

      const devices = new Map();
      for (const batch of this.alarmHistoryBatches) {
        const entries = Array.isArray(batch?.entries) ? batch.entries : [];
        const matching = entries
            .map(entry => this.withAlarmBatchContext(batch, entry))
            .filter(entry => this.entryHasAlarm(entry))
            .filter(entry => this.utcDayFromTimestamp(entry?.ts) === this.selectedAlarmDay);

        if (!matching.length) continue;
        const latest = matching[matching.length - 1];
        devices.set(String(batch?.ldn || latest?.ldn || '').toLowerCase(), this.decorateAlarmDevice({
          ...latest,
          ldn: batch?.ldn || latest?.ldn,
          last_ts: latest?.ts,
          last_alarm_bitmap: latest?.alarm_bitmap,
          last_active_alarms: latest?.active_alarms,
          last_leakage_duration: latest?.leakage_duration,
          last_pipe_burst_duration: latest?.pipe_burst_duration,
          last_backflow_duration: latest?.backflow_duration,
          last_temperature_duration: latest?.temperature_duration,
          last_dry_duration: latest?.dry_duration,
          last_overflow_duration: latest?.overflow_duration,
          alarm_state: latest?.alarm_state,
          persistent_alarms: latest?.persistent_alarms
        }));
      }

      return Array.from(devices.values()).filter(device => device.hasCurrentAlarm);
    },

    alarmVisibleDevices() {
      return this.selectedAlarmDay ? this.alarmDevicesForSelectedDay : this.currentAlarmDevices;
    },

    alarmFilteredDevices() {
      return this.alarmVisibleDevices.filter(device => {
        if (this.showPersistentAlarmsOnly && !device.hasPersistentAlarms) return false;
        if (!this.selectedAlarmTypeKey) return true;
        return Array.isArray(device?.alarmTypes) && device.alarmTypes.some(alarm => alarm.key === this.selectedAlarmTypeKey);
      });
    },

    alarmLocatedDevices() {
      return this.alarmFilteredDevices
          .map(device => {
            const location = this.alarmLocationIndex.get(String(device?.ldn || '').toLowerCase());
            return location ? { ...device, location } : null;
          })
          .filter(Boolean);
    },

    mostPersistentAlarmList() {
      return this.alarmFilteredDevices
          .map(device => {
            const persistentItems = this.getDevicePersistentAlarmItems(device);
            const durationItems = this.getAlarmDurationItems(device);

            const consecutiveScore = persistentItems.reduce((sum, alarm) => {
              return sum + Number(alarm?.consecutive_messages || alarm?.consecutiveMessages || 0);
            }, 0);

            const durationScore = durationItems.reduce((sum, item) => {
              return sum + Number(item?.value || 0);
            }, 0);

            const persistentReason =
                persistentItems
                    .map(alarm => this.formatPersistentAlarmReason(alarm))
                    .filter(Boolean)[0] || '';

            const persistentAlarmLabel =
                consecutiveScore > 0
                    ? `${consecutiveScore}x`
                    : durationItems[0]?.formatted || this.formatShortTime(device.last_ts);

            return {
              ...device,
              persistentItems,
              durationItems,
              persistentScore:
                  consecutiveScore * 100000 +
                  durationScore * 100 +
                  persistentItems.length * 10 +
                  Number(device.hasPersistentAlarms ? 1 : 0),
              persistentAlarmReason:
                  persistentReason ||
                  durationItems[0]?.formatted ||
                  'persistent warning',
              persistentAlarmLabel
            };
          })
          .filter(device => device.hasPersistentAlarms || device.persistentScore > 0)
          .sort((a, b) => {
            if (b.persistentScore !== a.persistentScore) {
              return b.persistentScore - a.persistentScore;
            }

            return this.timestampMs(b.last_ts) - this.timestampMs(a.last_ts);
          })
          .slice(0, 6);
    },

    alarmTimelineDays() {
      return this.alarmDailyOrdered.length ? this.alarmDailyOrdered : this.buildEmptyAlarmDayWindow();
    },

    alarmTimelineMax() {
      const max = Math.max(...this.alarmTimelineDays.map(item => Number(item?.count || 0)), 0);
      return max > 0 ? max : 1;
    },

    alarmTimelinePoints() {
      const sourceTop = 18;
      const sourceBottom = 104;
      const targetTop = 10;
      const targetBottom = 78;
      const lastIndex = Math.max(this.alarmTimelineDays.length - 1, 1);

      return this.alarmTimelineDays.map((item, index) => {
        const count = Number(item?.count || 0);
        const ratio = Math.max(0, Math.min(1, count / this.alarmTimelineMax));
        const xPct = Number(((index / lastIndex) * 100).toFixed(3));
        const sourceY = sourceBottom - ratio * (sourceBottom - sourceTop);
        const yRatio = (sourceY - sourceTop) / (sourceBottom - sourceTop);
        const yPct = Number((targetTop + yRatio * (targetBottom - targetTop)).toFixed(3));
        const [, month = '', day = ''] = String(item?.day || '').split('-');

        return {
          key: item?.day || `alarm-day-${index}`,
          day: item?.day,
          count,
          xPct,
          yPct,
          label: month && day ? `${month}-${day}` : ''
        };
      });
    },

    alarmTimelineLinePath() {
      const points = this.alarmTimelinePoints;
      if (!points.length) return '';
      if (points.length === 1) return `M ${points[0].xPct} ${points[0].yPct}`;

      let path = `M ${points[0].xPct} ${points[0].yPct}`;
      for (let index = 0; index < points.length - 1; index += 1) {
        const current = points[index];
        const next = points[index + 1];
        const midX = Number(((current.xPct + next.xPct) / 2).toFixed(3));
        path += ` C ${midX} ${current.yPct}, ${midX} ${next.yPct}, ${next.xPct} ${next.yPct}`;
      }
      return path;
    },

    alarmTimelineAreaPath() {
      if (!this.alarmTimelineLinePath || this.alarmTimelinePoints.length < 2) return '';
      const first = this.alarmTimelinePoints[0];
      const last = this.alarmTimelinePoints[this.alarmTimelinePoints.length - 1];
      return `${this.alarmTimelineLinePath} L ${last.xPct} 82 L ${first.xPct} 82 Z`;
    },

    alarmTimelineGuides() {
      return [
        { key: 'upper', y: 26 },
        { key: 'middle', y: 54 },
        { key: 'base', y: 82 }
      ];
    }
  },

  mounted() {
    this.initMap();
    this.fetchDashboardStats();
    this.statsInterval = setInterval(() => {
      this.fetchDashboardStats(true);
      if (this.dashboardMode === 'alarms') {
        this.loadAlarmData({ silent: true }).then(() => this.updateAlarmMarkers());
      }
    }, 30_000);
  },

  beforeUnmount() {
    if (this.statsInterval) clearInterval(this.statsInterval);
    this.cleanup();
  },

  methods: {
    dateToUtcDayKey(date) {
      const d = date instanceof Date ? date : new Date(date);
      const pad = n => String(n).padStart(2, '0');
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
    },

    utcDayKeyToDate(day) {
      const [year, month, date] = String(day || '').split('-').map(Number);
      return new Date(Date.UTC(year || 1970, (month || 1) - 1, date || 1));
    },

    utcDayFromTimestamp(ts) {
      if (!ts) return '';
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return '';
      return this.dateToUtcDayKey(d);
    },

    timestampMs(ts) {
      if (!ts) return 0;
      const d = new Date(ts);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    },

    formatShortTime(ts) {
      if (!ts) return 'N/A';
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return 'N/A';
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },

    setDashboardMode(mode) {
      if (mode !== 'devices' && mode !== 'alarms') return;
      this.dashboardMode = mode;
      this.applyDashboardMode();

      if (mode === 'alarms') {
        this.loadAlarmData({ silent: this.alarmLoaded }).then(() => {
          this.updateAlarmMarkers();
          this.fitMapToAlarmMarkers();
        });
      }
    },

    applyDashboardMode() {
      if (!this.map) return;

      if (this.dashboardMode === 'alarms') {
        this.setDeviceMapVisibility(false);
        this.attachAlarmClusterMoveWatcher();
        this.updateAlarmMarkers();
        return;
      }

      this.detachAlarmClusterMoveWatcher();
      this.clearAlarmMarkers();
      this.setDeviceMapVisibility(true);
      this.applyMapMode(this.show3d);
    },

    attachAlarmClusterMoveWatcher() {
      if (!this.map || this.alarmClusterMoveWatcher) return;

      this.alarmClusterMoveWatcher = () => {
        if (this.dashboardMode === 'alarms' && this.alarmMarkersClustered) {
          this.updateAlarmMarkers();
        }
      };

      this.map.on('moveend', this.alarmClusterMoveWatcher);
    },

    detachAlarmClusterMoveWatcher() {
      if (!this.map || !this.alarmClusterMoveWatcher) return;

      this.map.off('moveend', this.alarmClusterMoveWatcher);
      this.alarmClusterMoveWatcher = null;
    },

    setDeviceMapVisibility(visible) {
      for (const protocol of this.protocols) {
        [
          `hull-soft-fill-${protocol.key}`,
          `hull-core-${protocol.key}`,
          `hull-edge-${protocol.key}`,
          `hull-extrusion-${protocol.key}`,
          `mp-cluster-${protocol.key}`,
          `mp-count-${protocol.key}`,
          `mp-point-${protocol.key}`
        ].forEach(id => this.safeSetVisibility(id, visible));
      }

      this.toggleProtocolMarkers(visible && this.show3d);

      if (this.threeCapLayer) {
        this.threeCapLayer.visible = visible && this.show3d;
        this.map.triggerRepaint();
      }
    },

    async loadAlarmData({ silent = false } = {}) {
      if (!silent) this.alarmLoading = true;
      this.alarmHistoryLoading = true;

      try {
        const now = Math.floor(Date.now() / 1000);
        const fromUnix = now - this.alarmHistoryDaysWindow * 24 * 3600;
        const [snapshot, historyResult] = await Promise.all([
          AlarmApiService.listDevices(),
          AlarmApiService.listDevicesHistory({ fromUnix, toUnix: now }).catch((error) => {
            console.error('Failed to load alarm history:', error);
            return { devices: [] };
          })
        ]);

        this.alarmDevices = Array.isArray(snapshot?.devices) ? snapshot.devices.filter(Boolean) : [];
        this.alarmHistoryBatches = Array.isArray(historyResult?.devices) ? historyResult.devices.filter(Boolean) : [];
        this.alarmDailyOrdered = this.buildAlarmDayCounts(this.alarmHistoryBatches);
        if (this.selectedAlarmTypeKey && !this.alarmTypeCounts.some(alarm => alarm.key === this.selectedAlarmTypeKey)) {
          this.selectedAlarmTypeKey = null;
        }
        this.alarmLoaded = true;
      } catch (error) {
        console.error('Failed to load alarm data:', error);
        this.alarmDailyOrdered = this.buildEmptyAlarmDayWindow();
      } finally {
        this.alarmLoading = false;
        this.alarmHistoryLoading = false;
      }
    },

    buildEmptyAlarmDayWindow() {
      const out = [];
      const utcMid = Date.UTC(
          new Date().getUTCFullYear(),
          new Date().getUTCMonth(),
          new Date().getUTCDate()
      );

      for (let i = this.alarmHistoryDaysWindow - 1; i >= 0; i -= 1) {
        const d = new Date(utcMid - i * 24 * 3600 * 1000);
        out.push({ day: this.dateToUtcDayKey(d), count: 0 });
      }

      return out;
    },

    buildAlarmDayCounts(batches) {
      const days = this.buildEmptyAlarmDayWindow();
      const byDay = new Map(days.map(day => [day.day, 0]));

      for (const batch of batches || []) {
        const entries = Array.isArray(batch?.entries) ? batch.entries : [];

        for (const entry of entries) {
          const scopedEntry = this.withAlarmBatchContext(batch, entry);
          if (!this.entryHasAlarm(scopedEntry)) continue;
          const day = this.utcDayFromTimestamp(entry?.ts);
          if (!day || !byDay.has(day)) continue;
          byDay.set(day, byDay.get(day) + this.getAlarmQuantity(scopedEntry));
        }
      }

      return days.map(day => ({
        ...day,
        count: byDay.get(day.day) || 0
      }));
    },

    getAlarmQuantity(entry) {
      if (!this.entryHasAlarm(entry)) return 0;
      return Math.max(this.extractAlarmTypes(entry).length, 1);
    },

    selectAlarmDay(day) {
      this.selectedAlarmDay = day;
      this.updateAlarmMarkers();
      this.fitMapToAlarmMarkers();
    },

    selectAlarmType(key) {
      this.selectedAlarmTypeKey = this.selectedAlarmTypeKey === key ? null : key;
      this.updateAlarmMarkers();
      this.fitMapToAlarmMarkers();
    },

    toggleAlarmMarkerClustering() {
      this.alarmMarkersClustered = !this.alarmMarkersClustered;
      this.updateAlarmMarkers();
    },

    togglePersistentAlarmFilter() {
      this.showPersistentAlarmsOnly = !this.showPersistentAlarmsOnly;
      this.updateAlarmMarkers();
      this.fitMapToAlarmMarkers();
    },

    getMeteringPointDeviceLdn(mp) {
      const lad = mp?.last_active_device || mp?.lastActiveDevice || mp?.lastactivedevice || {};
      return (
          lad?.logical_device_number ||
          lad?.LogicalDeviceNumber ||
          lad?.ldn ||
          mp?.logical_device_number ||
          mp?.ldn ||
          ''
      );
    },

    normalizeAlarmList(value) {
      if (Array.isArray(value)) return value.filter(Boolean);
      if (value == null) return [];

      const raw = String(value).trim();
      if (!raw || raw === '[]') return [];

      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch {
        return [{ name: raw, description: raw }];
      }
    },

    withAlarmBatchContext(batch, entry) {
      if (!entry || typeof entry !== 'object') return entry;
      return {
        ...entry,
        frame_type: entry?.frame_type ?? batch?.frame_type ?? batch?.last_frame_type,
        last_frame_type: entry?.last_frame_type ?? batch?.last_frame_type ?? batch?.frame_type,
        protocol: entry?.protocol ?? batch?.protocol
      };
    },

    isMaddalenaAlarmEntry(raw) {
      const frameType = String(raw?.last_frame_type || raw?.frame_type || '').trim().toLowerCase();
      return frameType.startsWith('maddalena-electo/') || frameType.startsWith('maddalena-sj-evo/');
    },

    isDlmsAlarmEntry(raw) {
      const frameType = String(
          raw?.last_frame_type ||
          raw?.frame_type ||
          raw?.protocol ||
          raw?.last_protocol ||
          ''
      ).toLowerCase();
      return frameType === 'wtdlms' || frameType.includes('dlms');
    },

    isSuppressedDlmsAlarm(alarm) {
      if (!alarm) return false;

      const tokens = typeof alarm === 'object'
          ? [
            alarm.code,
            alarm.Code,
            alarm.key,
            alarm.Key,
            alarm.name,
            alarm.Name,
            alarm.label,
            alarm.Label,
            alarm.description,
            alarm.Description
          ]
          : [alarm];

      return tokens.map(value => String(value || '').trim()).filter(Boolean).some(token => {
        const code = token.toUpperCase().replace(/[\s-]+/g, '_');
        const lower = token.toLowerCase();
        return (
            SUPPRESSED_DLMS_ALARM_CODES.has(code) ||
            SUPPRESSED_DLMS_ALARM_KEYS.has(lower) ||
            SUPPRESSED_DLMS_ALARM_LABELS.has(lower)
        );
      });
    },

    filterDisplayAlarmList(raw, alarms) {
      const list = Array.isArray(alarms) ? alarms.filter(Boolean) : [];
      if (!this.isDlmsAlarmEntry(raw)) return list;
      return list.filter(alarm => !this.isSuppressedDlmsAlarm(alarm));
    },

    getDisplayAlarmBitmap(raw) {
      // Named alarms are authoritative for Maddalena. Raw mode/reserved bits
      // remain in the API response, but do not count as dashboard alarms.
      if (this.isMaddalenaAlarmEntry(raw)) return 0;
      let bitmap = Number(raw?.last_alarm_bitmap ?? raw?.alarm_bitmap ?? 0);
      if (!Number.isFinite(bitmap) || bitmap <= 0) return 0;

      if (this.isDlmsAlarmEntry(raw) && DLMS_DST_ACTIVE_ALARM) {
        bitmap &= ~(1 << DLMS_DST_ACTIVE_ALARM.bit);
      }

      return bitmap;
    },

    entryHasAlarm(entry) {
      if (!entry) return false;
      const activeAlarms = this.filterDisplayAlarmList(
          entry,
          this.normalizeAlarmList(entry.active_alarms ?? entry.last_active_alarms)
      );
      const alarmBitmap = this.getDisplayAlarmBitmap(entry);
      return (
          alarmBitmap > 0 ||
          activeAlarms.length > 0 ||
          Number(entry.leakage_duration || entry.last_leakage_duration || 0) > 0 ||
          Number(entry.pipe_burst_duration || entry.last_pipe_burst_duration || 0) > 0 ||
          Number(entry.backflow_duration || entry.last_backflow_duration || 0) > 0 ||
          Number(entry.temperature_duration || entry.last_temperature_duration || 0) > 0 ||
          Number(entry.dry_duration || entry.last_dry_duration || 0) > 0 ||
          Number(entry.overflow_duration || entry.last_overflow_duration || 0) > 0
      );
    },

    decorateAlarmDevice(raw) {
      const alarmTypes = this.extractAlarmTypes(raw);
      const persistentAlarms = this.filterDisplayAlarmList(
          raw,
          this.normalizePersistentAlarms(raw?.persistent_alarms ?? raw?.last_persistent_alarms)
      );
      const alarmState = String(raw?.alarm_state || raw?.last_alarm_state || '').toLowerCase();
      return {
        ...raw,
        hasCurrentAlarm: this.entryHasAlarm(raw),
        alarmTypes,
        primaryAlarmLabel: alarmTypes[0]?.label || 'Notification',
        primaryAlarmColor: alarmTypes[0]?.color || ALARM_COLORS.generic,
        alarmState: alarmState || (persistentAlarms.length ? 'warning' : 'normal'),
        persistentAlarms,
        hasPersistentAlarms: alarmState === 'warning' || persistentAlarms.length > 0
      };
    },

    normalizePersistentAlarms(value) {
      if (Array.isArray(value)) return value.filter(Boolean);
      if (value == null) return [];

      const raw = String(value).trim();
      if (!raw || raw === '[]') return [];

      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch {
        return [];
      }
    },

    formatPersistentAlarmReason(alarm) {
      if (!alarm || typeof alarm !== 'object') return '';

      const reasons = [];
      const consecutive = Number(alarm.consecutive_messages || alarm.consecutiveMessages || 0);
      if (consecutive > 0) reasons.push(`${consecutive} consecutive messages`);
      if (alarm.seen_in_last_6_days || alarm.seenInLast6Days) reasons.push('seen in last 6 days');

      return reasons.join(', ');
    },

    extractAlarmTypes(raw) {
      const out = [];
      const active = this.filterDisplayAlarmList(
          raw,
          this.normalizeAlarmList(raw?.last_active_alarms ?? raw?.active_alarms)
      );
      const isDlms = this.isDlmsAlarmEntry(raw);
      const definitions = isDlms ? DLMS_ALARM_DEFINITIONS : ALARM_DEFINITIONS;
      const byCode = this.isMaddalenaAlarmEntry(raw)
          ? MADDALENA_ALARM_BY_CODE
          : (isDlms ? DLMS_ALARM_BY_CODE : ALARM_BY_CODE);

      const add = (key, label, color) => {
        if (out.some(item => item.key === key)) return;
        out.push({ key, label, color });
      };

      const addDefinition = (definition, fallbackLabel) => {
        if (!definition) return false;
        if (isDlms && this.isSuppressedDlmsAlarm(definition)) return false;
        add(definition.key, fallbackLabel || definition.label, definition.color);
        return true;
      };

      for (const alarm of active) {
        const name = String(alarm?.name || alarm?.Name || (typeof alarm === 'string' ? alarm : '')).trim();
        const description = String(alarm?.description || alarm?.Description || name || 'Notification').trim();
        const lower = `${name} ${description}`.toLowerCase();
        const compactCode = name.toUpperCase().replace(/\s+/g, '');
        const normalizedCode = name.toUpperCase().replace(/[\s-]+/g, '_');
        if (addDefinition(byCode.get(compactCode) || byCode.get(normalizedCode), description)) continue;

        if (lower.includes('metrological')) add('pus', description, ALARM_COLORS.metrological);
        else if (lower.includes('empty pipe')) add('empty-pipe', description, ALARM_COLORS.emptyPipe);
        else if (lower.includes('back flow') || lower.includes('backflow')) add('backflow', description, ALARM_COLORS.backflow);
        else if (lower.includes('leak')) add('leak', description, ALARM_COLORS.leak);
        else if (lower.includes('burst')) add('burst', description, ALARM_COLORS.burst);
        else if (lower.includes('back')) add('backflow', description, ALARM_COLORS.backflow);
        else if (lower.includes('temp')) add('temperature', description, ALARM_COLORS.temperature);
        else if (lower.includes('battery')) add('low-battery', description, ALARM_COLORS.lowBattery);
        else add(name || description, description, ALARM_COLORS.generic);
      }

      const bitmap = this.getDisplayAlarmBitmap(raw);
      if (bitmap > 0) {
        for (const definition of definitions) {
          if ((bitmap & (1 << definition.bit)) !== 0) addDefinition(definition);
        }
      }

      if (Number(raw?.last_leakage_duration || raw?.leakage_duration || 0) > 0) add('leak-duration', 'Leakage duration', ALARM_COLORS.leak);
      if (Number(raw?.last_pipe_burst_duration || raw?.pipe_burst_duration || 0) > 0) add('burst-duration', 'Pipe burst duration', ALARM_COLORS.burst);
      if (Number(raw?.last_backflow_duration || raw?.backflow_duration || 0) > 0) add('backflow-duration', 'Backflow duration', ALARM_COLORS.backflow);
      if (Number(raw?.last_temperature_duration || raw?.temperature_duration || 0) > 0) add('temperature-duration', 'Temperature duration', ALARM_COLORS.temperature);
      if (Number(raw?.last_dry_duration || raw?.dry_duration || 0) > 0) add('dry-duration', 'Dry duration', ALARM_COLORS.dry);
      if (Number(raw?.last_overflow_duration || raw?.overflow_duration || 0) > 0) add('overflow-duration', 'Overflow duration', ALARM_COLORS.overflow);
      if (!out.length && bitmap > 0) add('bitmap', 'Notification bitmap', ALARM_COLORS.generic);

      return out;
    },

    getProtocolDefaults() {
      return PROTOCOL_DEFAULTS.map(item => ({ ...item }));
    },

    normalizeProtocolKey(value) {
      const raw = String(value || '').trim().toLowerCase();
      if (!raw) return null;

      if (raw.includes('dlms')) return 'dlms';
      if (raw.includes('lora') && raw.includes('wbus')) return 'lora_wbus';
      if (raw.includes('wbus') && raw.includes('lora')) return 'lora_wbus';
      if (raw.includes('wbus')) return 'wbus';
      if (raw.includes('ctr')) return 'ctr';
      if (raw.includes('lora')) return 'lora';

      return null;
    },

    mergeProtocols(apiProtocols = []) {
      const defaults = this.getProtocolDefaults();
      const merged = new Map(defaults.map(item => [item.key, item]));

      for (const item of apiProtocols) {
        const key = this.normalizeProtocolKey(item?.key || item?.name || item?.protocol);
        if (!key) continue;

        const base = merged.get(key) || {
          key,
          name: key,
          color: '#5bb6ff',
          count: 0
        };

        merged.set(key, {
          ...base,
          ...item,
          key,
          name: item?.name || base.name,
          color: item?.color || base.color,
          count: Number(item?.count || 0)
        });
      }

      return defaults.map(item => merged.get(item.key) || item);
    },

    normalizeCommData(rawData = {}) {
      const empty = {
        '24h': { dlms: 0, lora: 0, wbus: 0, ctr: 0, lora_wbus: 0 },
        '5d': { dlms: 0, lora: 0, wbus: 0, ctr: 0, lora_wbus: 0 },
        '15d': { dlms: 0, lora: 0, wbus: 0, ctr: 0, lora_wbus: 0 }
      };

      for (const tab of Object.keys(empty)) {
        const source = rawData?.[tab] || {};
        const normalized = {};

        for (const key of Object.keys(empty[tab])) {
          normalized[key] = Number(source[key] || 0);
        }

        empty[tab] = normalized;
      }

      return empty;
    },

    async fetchDashboardStats(silent = false) {
      if (!silent) this.statsLoading = true;
      this.statsError = null;

      try {
        const data = await DeviceApiServices.getDashboardStats();

        this.stats.totalDevices = Number(data?.total_devices || 0);
        this.stats.totalMpids = Number(data?.total_mpids || 0);
        this.stats.mpidsNoDevice = Number(data?.mpids_no_device || 0);
        this.stats.devicesNoMpid = Number(data?.devices_no_mpid || 0);
        this.stats.simOnline = Number(data?.sim_online >= 0 ? data.sim_online : 0);
        this.stats.simTotal = Number(data?.sim_total || 0);

        this.protocols = this.mergeProtocols(data?.protocols || []);
        this.commData = this.normalizeCommData(data?.comm_data || {});

        const maxCount = Math.max(...this.protocols.map(p => Number(p.count || 0)), 1);
        this.chartMax = Math.max(Math.ceil(maxCount / 500) * 500, 100);
        this.chartTicks = this.buildChartTicks(this.chartMax);
      } catch (error) {
        this.statsError = 'Failed to load stats';
        console.error('Dashboard stats error:', error);
      } finally {
        this.statsLoading = false;
      }
    },

    buildChartTicks(max) {
      const safeMax = Math.max(Number(max || 0), 100);
      const step = Math.max(Math.ceil(safeMax / 2 / 500) * 500, 500);
      const ticks = [];

      for (let value = 0; value <= safeMax; value += step) {
        ticks.push({
          val: value,
          label: value >= 1000 ? `${value / 1000}k` : String(value)
        });
      }

      if (ticks[ticks.length - 1]?.val !== safeMax) {
        ticks.push({
          val: safeMax,
          label: safeMax >= 1000 ? `${safeMax / 1000}k` : String(safeMax)
        });
      }

      return ticks;
    },

    initMap() {
      this.map = markRaw(
          new maplibregl.Map({
            container: this.$refs.mapContainer,
            style: this.styles[this.currentStyle],
            center: [15.24, 38.22],
            zoom: 11.1,
            pitch: 58,
            bearing: -22,
            antialias: true
          })
      );

      this.map.addControl(new maplibregl.NavigationControl(), 'top-right');

      this.map.once('load', async () => {
        this.mapIsReady = true;
        await this.loadMeteringPoints();
      });
    },

    async loadMeteringPoints() {
      if (!this.map || !this.mapIsReady) return;

      const buildVersion = ++this.layerBuildVersion;
      this.loading = true;
      this.error = null;

      try {
        const now = Date.now();

        if (
            METERS_CACHE.meteringPoints &&
            now - METERS_CACHE.meteringPointsAt < METERS_CACHE.TTL
        ) {
          this.meteringPoints = METERS_CACHE.meteringPoints;
        } else {
          const response = await MpidApiService.getMeteringPoints({ limit: 10000 });
          this.meteringPoints = response?.data || response?.metering_points || [];
          METERS_CACHE.meteringPoints = this.meteringPoints;
          METERS_CACHE.meteringPointsAt = now;
        }

        if (buildVersion !== this.layerBuildVersion || !this.map) return;

        if (!this.meteringPoints.length) {
          this.error = 'No metering points found';
          return;
        }

        this.removeDynamicMapLayers();

        if (buildVersion !== this.layerBuildVersion || !this.map) return;

        await this.addMapLayers(buildVersion);

        if (buildVersion !== this.layerBuildVersion || !this.map) return;

        this.initZoomWatcher();
        this.fitMapToPoints();
        this.applyDashboardMode();
      } catch (error) {
        if (buildVersion !== this.layerBuildVersion) return;
        console.error('Failed to load metering points:', error);
        this.error = error?.message || 'Failed to load metering points';
      } finally {
        if (buildVersion === this.layerBuildVersion) {
          this.loading = false;
        }
      }
    },

    buildProtocolBuckets() {
      const buckets = Object.fromEntries(this.protocols.map(p => [p.key, []]));

      for (const mp of this.meteringPoints) {
        const lat = parseFloat(mp?.lat);
        const lon = parseFloat(mp?.lon);

        if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

        const rawProtocol =
            mp?.last_active_device?.protocol ||
            mp?.lastActiveDevice?.protocol ||
            mp?.protocol ||
            '';

        const key = this.normalizeProtocolKey(rawProtocol);
        if (!key || !buckets[key]) continue;

        buckets[key].push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lon, lat]
          },
          properties: {
            mpid: mp?.mpid || 'N/A',
            customer: mp?.customer_fname || mp?.customerFName || 'N/A',
            street: mp?.street || 'N/A',
            city: mp?.city || 'N/A',
            zipcode: mp?.zip_code || mp?.zipcode || mp?.zipCode || 'N/A',
            protocol: key
          }
        });
      }

      return buckets;
    },

    stripInteriorRings(feature) {
      if (!feature?.geometry) return null;

      if (feature.geometry.type === 'Polygon') {
        return {
          ...feature,
          geometry: {
            type: 'Polygon',
            coordinates: [feature.geometry.coordinates[0]]
          }
        };
      }

      if (feature.geometry.type === 'MultiPolygon') {
        return {
          ...feature,
          geometry: {
            type: 'MultiPolygon',
            coordinates: feature.geometry.coordinates.map(poly => [poly[0]])
          }
        };
      }

      return feature;
    },

    clusterPointsByDistance(points) {
      const visited = new Array(points.length).fill(false);
      const clusters = [];

      for (let i = 0; i < points.length; i += 1) {
        if (visited[i]) continue;

        const queue = [i];
        const clusterIndexes = [i];
        visited[i] = true;

        while (queue.length) {
          const currentIndex = queue.shift();
          const [cx, cy] = points[currentIndex].geometry.coordinates;

          for (let j = 0; j < points.length; j += 1) {
            if (visited[j]) continue;

            const [jx, jy] = points[j].geometry.coordinates;
            const distance = Math.hypot(cx - jx, cy - jy);

            if (distance <= HULL_CLUSTER_EPSILON) {
              visited[j] = true;
              queue.push(j);
              clusterIndexes.push(j);
            }
          }
        }

        clusters.push(clusterIndexes.map(index => points[index]));
      }

      return clusters;
    },

    buildHullFeature(protocolPointFeatures) {
      const turfPoints = protocolPointFeatures.map(f => point(f.geometry.coordinates));
      if (!turfPoints.length) return null;

      const clusters = this.clusterPointsByDistance(turfPoints);
      const bufferedPolygons = [];

      for (const cluster of clusters) {
        if (cluster.length < 2) {
          // Single point — buffer into a small circle
          try {
            const b = buffer(cluster[0], 0.32, { units: 'kilometers' });
            if (b?.geometry) bufferedPolygons.push(b);
          } catch (error) {
            // skip
          }
          continue;
        }

        if (cluster.length === 2) {
          try {
            const bufferedA = buffer(cluster[0], 0.32, { units: 'kilometers' });
            const bufferedB = buffer(cluster[1], 0.32, { units: 'kilometers' });
            if (bufferedA?.geometry && bufferedB?.geometry) {
              const merged = union(featureCollection([bufferedA, bufferedB]));
              if (merged?.geometry) bufferedPolygons.push(merged);
            }
          } catch (error) {
            // skip
          }
          continue;
        }

        const fc = featureCollection(cluster);
        // Larger maxEdge = smoother, less jagged concave hull
        let concaveHull = null;
        try {
          concaveHull = concave(fc, { maxEdge: 3.1, units: 'kilometers' });
        } catch (error) {
          concaveHull = null;
        }
        const fallbackHull = convex(fc);
        const selectedHull =
            concaveHull?.geometry?.type === 'Polygon' ||
            concaveHull?.geometry?.type === 'MultiPolygon'
                ? concaveHull
                : fallbackHull;

        if (!selectedHull?.geometry) continue;

        // Buffer to smooth edges and round sharp corners
        try {
          const smoothed = buffer(selectedHull, 0.22, { units: 'kilometers' });
          if (smoothed?.geometry) bufferedPolygons.push(smoothed);
        } catch (error) {
          bufferedPolygons.push(selectedHull);
        }
      }

      if (!bufferedPolygons.length) return null;

      // Union all buffered polygons so overlapping clusters merge cleanly
      let mergedFeature;
      try {
        let merged = bufferedPolygons[0];
        for (let i = 1; i < bufferedPolygons.length; i += 1) {
          const result = union(featureCollection([merged, bufferedPolygons[i]]));
          if (result?.geometry) merged = result;
        }
        mergedFeature = {
          type: 'Feature',
          geometry: merged.geometry,
          properties: {}
        };
      } catch (error) {
        // Fallback: assemble as MultiPolygon without union
        mergedFeature = {
          type: 'Feature',
          geometry: {
            type: 'MultiPolygon',
            coordinates: bufferedPolygons.flatMap(f =>
                f.geometry.type === 'Polygon'
                    ? [f.geometry.coordinates]
                    : f.geometry.coordinates
            )
          },
          properties: {}
        };
      }

      // Strip interior rings so the Three.js caps (which only use outer rings)
      // match the MapLibre extrusion visually
      return this.stripInteriorRings(mergedFeature);
    },

    // Returns the extrusion base (floor height in meters) for a zone.
    // A zone "sits on top of" an earlier zone when most of its area (>=70%)
    // falls inside that earlier zone. Using area-overlap instead of strict
    // containment means zones that heavily overlap but aren't perfectly
    // nested still stack correctly.
    computeStackedBase(hullFeature, earlierItems) {
      let stackedBase = 0;

      let currentArea = 0;
      try {
        currentArea = area(hullFeature);
      } catch (error) {
        currentArea = 0;
      }

      if (currentArea <= 0) return 0;

      for (const previous of earlierItems) {
        let ratio = 0;

        try {
          const overlap = intersect(featureCollection([previous.feature, hullFeature]));
          if (overlap?.geometry) {
            const overlapArea = area(overlap);
            ratio = overlapArea / currentArea;
          }
        } catch (error) {
          // intersect can throw on certain MultiPolygon inputs — fall back
          // to a centroid-inside check so heavily nested zones still stack.
          try {
            const c = centroid(hullFeature);
            if (c?.geometry && booleanPointInPolygon(c, previous.feature)) {
              ratio = 1;
            }
          } catch (fallbackError) {
            ratio = 0;
          }
        }

        if (ratio >= 0.58 && previous.height > stackedBase) {
          stackedBase = previous.height;
        }
      }

      return stackedBase;
    },

    buildMarkerCoordinate(hullFeature) {
      try {
        const center = centroid(hullFeature);
        return center?.geometry?.coordinates || null;
      } catch {
        return null;
      }
    },

    hexToRgba(hex, alpha) {
      const normalized = String(hex || '').replace('#', '');

      if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
        return `rgba(255,255,255,${alpha})`;
      }

      const r = parseInt(normalized.slice(0, 2), 16);
      const g = parseInt(normalized.slice(2, 4), 16);
      const b = parseInt(normalized.slice(4, 6), 16);

      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    createProtocolMarkerElement(protocol) {
      const color = protocol.color || '#8b7cff';
      const glowSoft = this.hexToRgba(color, 0.18);
      const glowMid = this.hexToRgba(color, 0.34);
      const glowStrong = this.hexToRgba(color, 0.72);

      const root = document.createElement('div');
      root.className = 'protocol-callout-marker';

      const pill = document.createElement('div');
      pill.className = 'protocol-callout-pill';
      pill.style.boxShadow = `0 12px 34px rgba(0,0,0,0.34), 0 0 0 1px rgba(255,255,255,0.06), inset 0 1px 0 rgba(255,255,255,0.06)`;

      const icon = document.createElement('span');
      icon.className = 'protocol-callout-icon';
      icon.style.background = color;
      icon.style.boxShadow = `0 0 10px ${glowStrong}, 0 0 16px ${glowMid}`;

      const text = document.createElement('span');
      text.className = 'protocol-callout-text';
      text.textContent = protocol.name;

      pill.appendChild(icon);
      pill.appendChild(text);

      const stem = document.createElement('div');
      stem.className = 'protocol-callout-stem';
      stem.style.background = `linear-gradient(to bottom, ${glowStrong} 0%, rgba(255,255,255,0.14) 55%, rgba(255,255,255,0.02) 100%)`;
      stem.style.boxShadow = `0 0 10px ${glowSoft}`;

      const anchorWrap = document.createElement('div');
      anchorWrap.className = 'protocol-callout-anchor-wrap';

      const anchorGlow = document.createElement('span');
      anchorGlow.className = 'protocol-callout-anchor-glow';
      anchorGlow.style.background = color;
      anchorGlow.style.boxShadow = `0 0 12px ${glowMid}, 0 0 26px ${glowSoft}`;
      anchorGlow.style.opacity = '0.34';

      const anchor = document.createElement('span');
      anchor.className = 'protocol-callout-anchor';
      anchor.style.background = color;
      anchor.style.boxShadow = `0 0 14px ${glowStrong}, 0 0 24px ${glowMid}`;

      anchorWrap.appendChild(anchorGlow);
      anchorWrap.appendChild(anchor);

      root.appendChild(pill);
      root.appendChild(stem);
      root.appendChild(anchorWrap);

      return root;
    },

    createAlarmMarkerElement(device) {
      const color = device.primaryAlarmColor || ALARM_COLORS.generic;
      const glowStrong = this.hexToRgba(color, 0.82);

      const root = document.createElement('div');
      root.className = 'protocol-callout-marker alarm-callout-marker';

      const pill = document.createElement('div');
      pill.className = 'protocol-callout-pill alarm-callout-pill';

      const icon = document.createElement('span');
      icon.className = 'protocol-callout-icon alarm-callout-icon';
      icon.style.background = color;

      const text = document.createElement('span');
      text.className = 'protocol-callout-text alarm-callout-text';
      text.textContent = device.primaryAlarmLabel || device.ldn || 'Notification';

      pill.appendChild(icon);
      pill.appendChild(text);

      const stem = document.createElement('div');
      stem.className = 'protocol-callout-stem alarm-callout-stem';
      stem.style.background = `linear-gradient(to bottom, ${glowStrong} 0%, rgba(255,255,255,0.16) 55%, rgba(255,255,255,0.02) 100%)`;

      const anchorWrap = document.createElement('div');
      anchorWrap.className = 'protocol-callout-anchor-wrap';

      const anchorGlow = document.createElement('span');
      anchorGlow.className = 'protocol-callout-anchor-glow';
      anchorGlow.style.background = color;
      anchorGlow.style.opacity = '0.42';

      const anchor = document.createElement('span');
      anchor.className = 'protocol-callout-anchor';
      anchor.style.background = color;

      anchorWrap.appendChild(anchorGlow);
      anchorWrap.appendChild(anchor);

      root.appendChild(pill);
      root.appendChild(stem);
      root.appendChild(anchorWrap);

      return root;
    },

    createAlarmClusterElement(group) {
      const color = group.primaryColor || ALARM_COLORS.generic;
      const glowStrong = this.hexToRgba(color, 0.82);

      const root = document.createElement('div');
      root.className = 'protocol-callout-marker alarm-callout-marker alarm-cluster-marker';

      const pill = document.createElement('div');
      pill.className = 'protocol-callout-pill alarm-callout-pill alarm-cluster-pill';

      const count = document.createElement('span');
      count.className = 'alarm-cluster-count';
      count.style.background = color;
      count.textContent = String(group.devices.length);

      const text = document.createElement('span');
      text.className = 'protocol-callout-text alarm-callout-text';
      text.textContent = group.primaryAlarmLabel || 'Alarm';

      pill.appendChild(count);
      pill.appendChild(text);

      const stem = document.createElement('div');
      stem.className = 'protocol-callout-stem alarm-callout-stem';
      stem.style.background = `linear-gradient(to bottom, ${glowStrong} 0%, rgba(255,255,255,0.16) 55%, rgba(255,255,255,0.02) 100%)`;

      const anchorWrap = document.createElement('div');
      anchorWrap.className = 'protocol-callout-anchor-wrap';

      const anchorGlow = document.createElement('span');
      anchorGlow.className = 'protocol-callout-anchor-glow';
      anchorGlow.style.background = color;
      anchorGlow.style.opacity = '0.42';

      const anchor = document.createElement('span');
      anchor.className = 'protocol-callout-anchor';
      anchor.style.background = color;

      anchorWrap.appendChild(anchorGlow);
      anchorWrap.appendChild(anchor);

      root.appendChild(pill);
      root.appendChild(stem);
      root.appendChild(anchorWrap);

      return root;
    },

    getAlarmClusterSummary(devices = []) {
      const counts = new Map();

      for (const device of devices) {
        const alarmTypes = Array.isArray(device?.alarmTypes) && device.alarmTypes.length
            ? device.alarmTypes
            : [{
              key: device?.primaryAlarmLabel || 'alarm',
              label: device?.primaryAlarmLabel || 'Alarm',
              color: device?.primaryAlarmColor || ALARM_COLORS.generic
            }];

        const alarm =
            (this.selectedAlarmTypeKey && alarmTypes.find(item => item.key === this.selectedAlarmTypeKey)) ||
            alarmTypes[0];
        const label = String(alarm?.label || device?.primaryAlarmLabel || 'Alarm').trim() || 'Alarm';
        const key = `${alarm?.key || label}`.toLowerCase();
        const current = counts.get(key) || {
          label,
          color: alarm?.color || device?.primaryAlarmColor || ALARM_COLORS.generic,
          count: 0
        };

        current.count += 1;
        counts.set(key, current);
      }

      return Array.from(counts.values()).sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.label.localeCompare(b.label);
      })[0] || {
        label: 'Alarm',
        color: ALARM_COLORS.generic
      };
    },

    buildAlarmMarkerGroups(devices) {
      if (!this.alarmMarkersClustered || !this.map) {
        return devices.map(device => ({
          type: 'device',
          devices: [device],
          lngLat: [device.location.lon, device.location.lat],
          primaryAlarmLabel: device.primaryAlarmLabel || 'Alarm',
          primaryColor: device.primaryAlarmColor || ALARM_COLORS.generic
        }));
      }

      const thresholdPx = 74;
      const groups = [];

      for (const device of devices) {
        const lngLat = [device.location.lon, device.location.lat];
        const projected = this.map.project(lngLat);
        let target = null;

        for (const group of groups) {
          const distance = Math.hypot(group.x - projected.x, group.y - projected.y);
          if (distance <= thresholdPx) {
            target = group;
            break;
          }
        }

        if (!target) {
          groups.push({
            type: 'cluster',
            devices: [device],
            x: projected.x,
            y: projected.y,
            sumX: projected.x,
            sumY: projected.y,
            lngLat,
            primaryAlarmLabel: device.primaryAlarmLabel || 'Alarm',
            primaryColor: device.primaryAlarmColor || ALARM_COLORS.generic
          });
          continue;
        }

        target.devices.push(device);
        target.sumX += projected.x;
        target.sumY += projected.y;
        target.x = target.sumX / target.devices.length;
        target.y = target.sumY / target.devices.length;
        const center = this.map.unproject([target.x, target.y]);
        target.lngLat = [center.lng, center.lat];
      }

      return groups.map(group => {
        if (group.devices.length === 1) {
          const device = group.devices[0];
          return {
            type: 'device',
            devices: group.devices,
            lngLat: [device.location.lon, device.location.lat],
            primaryAlarmLabel: device.primaryAlarmLabel || 'Alarm',
            primaryColor: device.primaryAlarmColor || ALARM_COLORS.generic
          };
        }

        const summary = this.getAlarmClusterSummary(group.devices);
        return {
          ...group,
          primaryAlarmLabel: summary.label,
          primaryColor: summary.color
        };
      });
    },

    escapeHtml(value) {
      return String(value ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
    },

    getDeviceUrl(ldn) {
      const origin = window?.location?.origin || 'https://metersuite.wtmeters.it';
      return `${origin}/devices/${encodeURIComponent(String(ldn || ''))}`;
    },

    getAlarmDurationItems(raw) {
      return ALARM_DURATION_FIELDS
          .map(field => {
            const value = Number(raw?.[field.lastKey] ?? raw?.[field.key] ?? 0);
            if (!Number.isFinite(value) || value <= 0) return null;
            return {
              label: field.label,
              value,
              formatted: this.formatAlarmDuration(value)
            };
          })
          .filter(Boolean);
    },

    formatAlarmDuration(value) {
      const totalMinutes = Math.max(0, Math.floor(Number(value) || 0));
      const days = Math.floor(totalMinutes / 1440);
      const hours = Math.floor((totalMinutes % 1440) / 60);
      const minutes = totalMinutes % 60;
      const parts = [];

      if (days) parts.push(`${days}d`);
      if (hours) parts.push(`${hours}h`);
      if (minutes) parts.push(`${minutes}m`);

      if (!parts.length || parts.join(' ') === `${totalMinutes}m`) {
        return `${totalMinutes}m`;
      }

      return `${totalMinutes}m (${parts.join(' ')})`;
    },

    buildAlarmDurationRowsHtml(device) {
      const rows = this.getAlarmDurationItems(device)
          .map(item => `
            <div class="popup-row alarm-duration-row">
              <span class="popup-label">${this.escapeHtml(item.label)}</span>
              <span class="popup-value">${this.escapeHtml(item.formatted)}</span>
            </div>
          `)
          .join('');

      if (!rows) return '';

      return `
        <div class="popup-section-title alarm-duration-title">Duration</div>
        ${rows}
      `;
    },

    buildPersistentAlarmRowsHtml(device) {
      const rows = this.getDevicePersistentAlarmItems(device)
          .map(alarm => {
            const name = this.escapeHtml(alarm?.description || alarm?.Description || alarm?.name || alarm?.Name || 'Persistent notification');
            const reason = this.escapeHtml(this.formatPersistentAlarmReason(alarm) || 'persistent warning');
            return `
              <div class="persistent-alarm-row">
                <span>${name}</span>
                <strong>${reason}</strong>
              </div>
            `;
          })
          .join('');

      if (!rows) return '';

      return `
        <div class="popup-section-title persistent-alarm-title">Persistent</div>
        ${rows}
      `;
    },

    getDevicePersistentAlarmItems(device) {
      return this.filterDisplayAlarmList(
          device,
          this.normalizePersistentAlarms(
              device?.persistentAlarms ??
              device?.persistent_alarms ??
              device?.last_persistent_alarms
          )
      );
    },

    getDevicePersistentAlarmStatus(device) {
      const alarms = this.getDevicePersistentAlarmItems(device);
      const state = String(device?.alarmState || device?.alarm_state || device?.last_alarm_state || '').toLowerCase();

      if (!alarms.length && state !== 'warning') return null;

      const first = alarms[0] || {};
      const label =
          first?.description ||
          first?.Description ||
          first?.name ||
          first?.Name ||
          'Persistent notification';
      const reason = alarms.length
          ? this.formatPersistentAlarmReason(first) || 'persistent warning'
          : 'warning state';

      return {
        count: alarms.length,
        label,
        reason
      };
    },

    buildAlarmDevicePopupHtml(device) {
      const ldn = this.escapeHtml(device.ldn || 'N/A');
      const location = device.location || {};
      const alarms = Array.isArray(device.alarmTypes) && device.alarmTypes.length
          ? device.alarmTypes.map(alarm => this.escapeHtml(alarm.label)).join(', ')
          : 'Notification';
      const deviceUrl = this.getDeviceUrl(device.ldn);
      const durationRows = this.buildAlarmDurationRowsHtml(device);
      const persistentRows = this.buildPersistentAlarmRowsHtml(device);

      return `
        <div class="mpid-popup alarm-popup">
          <div class="popup-header">
            <div class="popup-mpid-label">Device Notification</div>
            <div class="popup-title">${ldn}</div>
          </div>
          <div class="popup-section">
            <div class="popup-row">
              <span class="popup-label">Notification</span>
              <span class="popup-value">${alarms}</span>
            </div>
            ${durationRows}
            ${persistentRows}
            <div class="popup-row">
              <span class="popup-label">Frame</span>
              <span class="popup-value">${this.escapeHtml(device.last_frame_type || device.frame_type || 'N/A')}</span>
            </div>
            <div class="popup-row">
              <span class="popup-label">Last seen</span>
              <span class="popup-value">${this.escapeHtml(this.formatShortTime(device.last_ts || device.ts))}</span>
            </div>
            <div class="popup-row">
              <span class="popup-label">MPID</span>
              <span class="popup-value">${this.escapeHtml(location.mpid || 'N/A')}</span>
            </div>
            <div class="popup-row">
              <span class="popup-label">Address</span>
              <span class="popup-value">${this.escapeHtml(location.street || 'N/A')}, ${this.escapeHtml(location.city || 'N/A')}</span>
            </div>
          </div>
          <a class="popup-device-link" href="${this.escapeHtml(deviceUrl)}" target="_blank" rel="noopener noreferrer">Open device</a>
        </div>
      `;
    },

    buildAlarmClusterPopupHtml(devices) {
      const rows = devices
          .slice()
          .map(device => ({
            device,
            persistent: this.getDevicePersistentAlarmStatus(device)
          }))
          .sort((a, b) => {
            const persistentDelta = Number(Boolean(b.persistent)) - Number(Boolean(a.persistent));
            if (persistentDelta) return persistentDelta;
            return this.timestampMs(b.device.last_ts || b.device.ts) - this.timestampMs(a.device.last_ts || a.device.ts);
          })
          .slice(0, 8)
          .map(({ device, persistent }) => {
            const ldn = this.escapeHtml(device.ldn || 'N/A');
            const label = this.escapeHtml(device.primaryAlarmLabel || 'Notification');
            const url = this.escapeHtml(this.getDeviceUrl(device.ldn));
            const persistentCount = Number(persistent?.count || 0);
            const persistentMore = persistentCount > 1 ? ` +${persistentCount - 1}` : '';
            const persistentDetail = persistent
                ? `${this.escapeHtml(persistent.label)}${persistent.reason ? ` · ${this.escapeHtml(persistent.reason)}` : ''}${this.escapeHtml(persistentMore)}`
                : '';
            const persistentBadge = persistent
                ? '<span class="alarm-cluster-persistent-badge">Persistent</span>'
                : '';
            const persistentLine = persistent
                ? `<small>${persistentDetail}</small>`
                : '';
            return `
              <a class="alarm-cluster-popup-row${persistent ? ' has-persistent' : ''}" href="${url}" target="_blank" rel="noopener noreferrer">
                <span class="alarm-cluster-device-cell">
                  <span class="alarm-cluster-device-main">
                    <span class="alarm-cluster-ldn">${ldn}</span>
                    ${persistentBadge}
                  </span>
                  ${persistentLine}
                </span>
                <strong>${label}</strong>
              </a>
            `;
          })
          .join('');
      const persistentDevices = devices.filter(device => this.getDevicePersistentAlarmStatus(device)).length;
      const persistentSummary = `
        <div class="alarm-cluster-persistent-summary${persistentDevices ? ' has-persistent' : ''}">
          <span>Persistent notifications</span>
          <strong>${persistentDevices.toLocaleString()} / ${devices.length.toLocaleString()} devices</strong>
        </div>
      `;
      const more = devices.length > 8 ? `<div class="alarm-cluster-popup-more">+${devices.length - 8} more</div>` : '';

      return `
        <div class="mpid-popup alarm-popup">
          <div class="popup-header">
            <div class="popup-mpid-label">Notification Cluster</div>
            <div class="popup-title">${devices.length.toLocaleString()} devices</div>
          </div>
          <div class="popup-section alarm-cluster-popup-list">
            ${persistentSummary}
            ${rows}
            ${more}
          </div>
        </div>
      `;
    },

    getAlarmPopupKey(group) {
      if (!group?.devices?.length) return '';
      if (group.type === 'cluster' && group.devices.length > 1) {
        return `cluster:${group.devices.map(device => device?.ldn || '').filter(Boolean).sort().join('|')}`;
      }

      return `device:${group.devices[0]?.ldn || ''}`;
    },

    getOpenAlarmPopupKey() {
      if (this.activeAlarmPopupKey) return this.activeAlarmPopupKey;

      for (const marker of this.alarmMarkers) {
        const popup = marker?.getPopup?.();
        if (popup?.isOpen?.()) return popup.__alarmPopupKey || null;
      }

      return null;
    },

    updateAlarmMarkers() {
      if (!this.map || this.dashboardMode !== 'alarms') return;
      const openPopupKey = this.getOpenAlarmPopupKey();
      let reopenedPopup = false;
      this.suppressAlarmPopupCloseTracking = true;
      this.clearAlarmMarkers();
      this.suppressAlarmPopupCloseTracking = false;
      this.activeAlarmPopupKey = openPopupKey;

      for (const group of this.buildAlarmMarkerGroups(this.alarmLocatedDevices)) {
        const device = group.devices[0];
        const isCluster = group.type === 'cluster' && group.devices.length > 1;
        const popupKey = this.getAlarmPopupKey(group);
        const element = isCluster ? this.createAlarmClusterElement(group) : this.createAlarmMarkerElement(device);
        const popup = new maplibregl.Popup({
          offset: 26,
          closeButton: true,
          closeOnClick: true,
          maxWidth: '320px'
        }).setHTML(isCluster ? this.buildAlarmClusterPopupHtml(group.devices) : this.buildAlarmDevicePopupHtml(device));
        popup.__alarmPopupKey = popupKey;
        popup.on('open', () => {
          this.activeAlarmPopupKey = popupKey;
        });
        popup.on('close', () => {
          if (!this.suppressAlarmPopupCloseTracking && this.activeAlarmPopupKey === popupKey) {
            this.activeAlarmPopupKey = null;
          }
        });

        const marker = new maplibregl.Marker({
          element,
          anchor: 'bottom'
        })
            .setLngLat(group.lngLat)
            .setPopup(popup)
            .addTo(this.map);

        this.alarmMarkers.push(marker);

        if (openPopupKey && popupKey === openPopupKey) {
          marker.togglePopup();
          reopenedPopup = true;
        }
      }

      if (openPopupKey && !reopenedPopup) {
        this.activeAlarmPopupKey = null;
      }
    },

    clearAlarmMarkers() {
      for (const marker of this.alarmMarkers) {
        try {
          marker.remove();
        } catch (error) {
          // ignore marker cleanup failures
        }
      }

      this.alarmMarkers = [];
    },

    fitMapToAlarmMarkers() {
      if (!this.map || this.dashboardMode !== 'alarms' || !this.alarmLocatedDevices.length) return;

      const bounds = new maplibregl.LngLatBounds();
      for (const device of this.alarmLocatedDevices) {
        bounds.extend([device.location.lon, device.location.lat]);
      }

      if (bounds.isEmpty()) return;
      this.map.fitBounds(bounds, {
        padding: { top: 150, bottom: 230, left: 360, right: 360 },
        minZoom: 11,
        maxZoom: 15,
        duration: 900
      });
    },

    clearProtocolMarkers() {
      for (const marker of this.protocolMarkers) {
        try {
          marker.remove();
        } catch (error) {
          // ignore marker cleanup failures
        }
      }

      this.protocolMarkers = [];
    },

    toggleProtocolMarkers(visible) {
      for (const marker of this.protocolMarkers) {
        const element = marker.getElement?.();
        if (!element) continue;
        element.style.display = visible ? '' : 'none';
      }
    },

    safeAddSource(id, source) {
      if (!this.map) return;

      if (this.map.getSource(id)) {
        const existingSource = this.map.getSource(id);
        if (source?.type === 'geojson' && existingSource?.setData) {
          existingSource.setData(source.data);
        }
        return;
      }

      this.map.addSource(id, source);
    },

    safeAddLayer(layer) {
      if (!this.map || this.map.getLayer(layer.id)) return;
      this.map.addLayer(layer);
    },

    safeRemoveLayer(id) {
      try {
        if (this.map?.getLayer(id)) this.map.removeLayer(id);
      } catch (error) {
        console.warn(`Failed to remove layer ${id}:`, error);
      }
    },

    safeRemoveSource(id) {
      try {
        if (this.map?.getSource(id)) this.map.removeSource(id);
      } catch (error) {
        console.warn(`Failed to remove source ${id}:`, error);
      }
    },

    safeSetVisibility(id, visible) {
      if (!this.map?.getLayer(id)) return;
      this.map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
    },

    removeThreeCapLayer() {
      if (!this.threeCapLayer) return;

      const layer = this.threeCapLayer;

      try {
        if (this.map?.getLayer('three-top-caps')) {
          this.map.removeLayer('three-top-caps');
        }
      } catch (error) {
        console.warn('Failed to remove three top cap layer:', error);
      } finally {
        try {
          layer.disposeResources?.();
        } catch (disposeError) {
          console.warn('Failed to dispose three top cap resources:', disposeError);
        }
        this.threeCapLayer = null;
      }
    },

    mixHexColors(hexA, hexB, amount = 0.5) {
      const normalize = value => {
        const safe = String(value || '').replace('#', '');
        return /^[0-9a-fA-F]{6}$/.test(safe) ? safe : 'ffffff';
      };

      const a = normalize(hexA);
      const b = normalize(hexB);
      const t = Math.max(0, Math.min(1, Number(amount) || 0));

      const ar = parseInt(a.slice(0, 2), 16);
      const ag = parseInt(a.slice(2, 4), 16);
      const ab = parseInt(a.slice(4, 6), 16);
      const br = parseInt(b.slice(0, 2), 16);
      const bg = parseInt(b.slice(2, 4), 16);
      const bb = parseInt(b.slice(4, 6), 16);

      const r = Math.round(ar + (br - ar) * t);
      const g = Math.round(ag + (bg - ag) * t);
      const bch = Math.round(ab + (bb - ab) * t);

      return `#${[r, g, bch].map(v => v.toString(16).padStart(2, '0')).join('')}`;
    },

    buildRingVectors(outerRing, originMerc, z) {
      return outerRing.map(([lng, lat]) => {
        const mc = maplibregl.MercatorCoordinate.fromLngLat([lng, lat], 0);
        return new THREE.Vector3(mc.x - originMerc.x, mc.y - originMerc.y, z);
      });
    },

    scaleRingVectors(points, factor = 1) {
      if (!Array.isArray(points) || !points.length || factor === 1) {
        return points.map(point => point.clone());
      }

      const center = points.reduce(
          (acc, point) => acc.add(point),
          new THREE.Vector3(0, 0, 0)
      ).multiplyScalar(1 / points.length);

      return points.map(point => {
        const next = point.clone().sub(center).multiplyScalar(factor).add(center);
        next.z = point.z;
        return next;
      });
    },

    addLineLoop(scene, disposables, points, materialConfig, renderOrder) {
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.LineBasicMaterial(materialConfig);
      const line = new THREE.LineLoop(geometry, material);
      line.renderOrder = renderOrder;
      scene.add(line);
      disposables.push(geometry, material);
      return line;
    },

    addEdgeStack(scene, disposables, topRingPoints, bottomRingPoints, color, baseRenderOrder) {
      // Top edge: bright version of the zone color with additive blending for neon glow.
      const topColor = this.mixHexColors(color, '#ffffff', 0.55);
      this.addLineLoop(
          scene,
          disposables,
          topRingPoints,
          {
            color: topColor,
            transparent: true,
            opacity: 0.95,
            depthTest: false,
            depthWrite: false,
            blending: THREE.AdditiveBlending
          },
          baseRenderOrder + 10
      );

      // Bottom edge: single dim line — noticeably less glow than the top edge
      this.addLineLoop(
          scene,
          disposables,
          bottomRingPoints,
          {
            color,
            transparent: true,
            opacity: 0.07,
            depthTest: false,
            depthWrite: false,
            blending: THREE.AdditiveBlending
          },
          baseRenderOrder + 11
      );
    },

    createDotShaderMaterial(capDotColor, metersToMerc) {
      const uniforms = {
        uMetersPerPixel: { value: 60.0 },
        uMetersToMerc:   { value: metersToMerc },
        uColor:          { value: new THREE.Color(capDotColor) },
        uDotSpacingPx:   { value: ZONE_DOT_SCREEN_SPACING_PX },
        uDotRadiusPx:    { value: ZONE_DOT_SCREEN_RADIUS_PX }
      };

      return new THREE.ShaderMaterial({
        uniforms,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
        vertexShader: `
      varying vec2 vWorldMeters;
      uniform float uMetersToMerc;
      void main() {
        vWorldMeters = position.xy / uMetersToMerc;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
        fragmentShader: `
      precision highp float;
      varying vec2 vWorldMeters;
      uniform float uMetersPerPixel;
      uniform float uDotSpacingPx;
      uniform float uDotRadiusPx;
      uniform vec3  uColor;

      float hash12(vec2 p) {
        vec3 p3 = fract(vec3(p.xyx) * 0.1031);
        p3 += dot(p3, p3.yzx + 33.33);
        return fract((p3.x + p3.y) * p3.z);
      }

      mat2 rot(float a) {
        float s = sin(a);
        float c = cos(a);
        return mat2(c, -s, s, c);
      }

      void main() {
        float spacingX = uDotSpacingPx * uMetersPerPixel;
        float spacingY = spacingX * 0.90;
        float radiusM = uDotRadiusPx * uMetersPerPixel;

        vec2 uv = rot(radians(17.0)) * vWorldMeters;
        vec2 cellSize = vec2(spacingX, spacingY);
        vec2 cell = floor((uv + cellSize * 0.5) / cellSize);

        float odd = mod(cell.y, 2.0);
        uv.x += odd * spacingX * 0.5;

        vec2 local = mod(uv + cellSize * 0.5, cellSize) - cellSize * 0.5;
        float jitter = hash12(cell) - 0.5;
        float localRadius = radiusM * (0.84 + jitter * 0.18);
        float edge = max(uMetersPerPixel * 0.85, 0.0005);

        float d = length(local);
        float core = 1.0 - smoothstep(localRadius - edge, localRadius + edge, d);
        float halo = 1.0 - smoothstep(localRadius * 1.45, localRadius * 2.9, d);

        float alpha = core * 0.44 + halo * 0.10;
        gl_FragColor = vec4(uColor, clamp(alpha, 0.0, 0.54));
      }
    `
      });
    },

    addCustomTopCapLayer(capItems) {
      if (!this.map || !capItems.length) return;

      this.removeThreeCapLayer();

      let sumLng = 0;
      let sumLat = 0;
      let coordCount = 0;

      for (const { feature } of capItems) {
        const polygonSets =
            feature.geometry.type === 'Polygon'
                ? [feature.geometry.coordinates]
                : feature.geometry.coordinates;

        for (const rings of polygonSets) {
          const outerRing = rings[0] || [];
          for (const [lng, lat] of outerRing) {
            sumLng += lng;
            sumLat += lat;
            coordCount += 1;
          }
        }
      }

      if (!coordCount) return;

      const originLng = sumLng / coordCount;
      const originLat = sumLat / coordCount;
      const originMerc = maplibregl.MercatorCoordinate.fromLngLat([originLng, originLat], 0);
      const metersToMerc = originMerc.meterInMercatorCoordinateUnits();

      const scene = new THREE.Scene();
      scene.autoUpdate = false;

      const disposables = [];
      const dotMaterials = [];

      for (let itemIndex = 0; itemIndex < capItems.length; itemIndex += 1) {
        const { color, base, height, feature, stackIndex } = capItems[itemIndex];
        const lowerCaps = capItems.slice(0, itemIndex);

        const polygonSets =
            feature.geometry.type === 'Polygon'
                ? [feature.geometry.coordinates]
                : feature.geometry.coordinates;

        const baseZ = (height + ZONE_TOP_CAP_BASE_LIFT_METERS) * metersToMerc;
        const dotZ = (height + ZONE_TOP_CAP_DOT_LIFT_METERS) * metersToMerc;
        const topEdgeZ = (height + ZONE_TOP_CAP_EDGE_LIFT_METERS) * metersToMerc;
        const groundEdgeZ = (base + ZONE_BOTTOM_EDGE_LIFT_METERS) * metersToMerc;
        const baseRenderOrder = stackIndex * 100;
        const capBaseColor = this.mixHexColors(color, '#071019', 0.56);
        const capDotColor = this.mixHexColors(color, '#ffffff', 0.16);
        const capHaloColor = this.mixHexColors(color, '#ffffff', 0.06);

        for (const rings of polygonSets) {
          const outerRing = rings[0];
          if (!outerRing?.length) continue;

          const shape = new THREE.Shape();

          outerRing.forEach(([lng, lat], index) => {
            const mc = maplibregl.MercatorCoordinate.fromLngLat([lng, lat], 0);
            const x = mc.x - originMerc.x;
            const y = mc.y - originMerc.y;

            if (index === 0) shape.moveTo(x, y);
            else shape.lineTo(x, y);
          });

          const baseGeometry = new THREE.ShapeGeometry(shape);
          disposables.push(baseGeometry);

          const baseMaterial = new THREE.MeshBasicMaterial({
            color: capBaseColor,
            transparent: true,
            opacity: 0.46,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false,
            blending: THREE.NormalBlending,
            polygonOffset: true,
            polygonOffsetFactor: 3,
            polygonOffsetUnits: 3
          });
          disposables.push(baseMaterial);

          const baseCap = new THREE.Mesh(baseGeometry, baseMaterial);
          baseCap.position.z = baseZ;
          baseCap.renderOrder = baseRenderOrder + 1;
          scene.add(baseCap);


          const dotGeometry = baseGeometry.clone();
          disposables.push(dotGeometry);
          const dotMaterial = this.createDotShaderMaterial(capDotColor, metersToMerc);
          disposables.push(dotMaterial);
          dotMaterials.push(dotMaterial);

          const dotCap = new THREE.Mesh(dotGeometry, dotMaterial);
          dotCap.position.z = dotZ;
          dotCap.renderOrder = baseRenderOrder + 3;
          scene.add(dotCap);

          const haloGeometry = baseGeometry.clone();
          disposables.push(haloGeometry);
          const haloMaterial = new THREE.MeshBasicMaterial({
            color: capHaloColor,
            transparent: true,
            opacity: 0.0,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            polygonOffset: true,
            polygonOffsetFactor: -4,
            polygonOffsetUnits: -4
          });
          disposables.push(haloMaterial);



          const topRingPoints = this.buildRingVectors(outerRing, originMerc, topEdgeZ);

          // Determine the bottom edge Z for the entire ring.
          // If this polygon is stacked (base > 0), base already equals the lower
          // polygon's extrusion height (computed by computeStackedBase). Use it
          // directly — no point-in-polygon check needed and no risk of boundary
          // failures making the edge snap to the floor.
          // For non-stacked polygons (base == 0) fall through to per-point checks
          // so any partial overlap with a lower cap still gets elevated correctly.
          let bottomZ;
          if (base > 0) {
            // Stacked polygon: bottom edge sits on the lower polygon's cap top surface
            bottomZ = (base + ZONE_TOP_CAP_EDGE_LIFT_METERS) * metersToMerc;
          } else {
            // Non-stacked: check per-point whether it's above a lower cap
            // (handles partial overlaps that didn't meet the stacking threshold)
            bottomZ = groundEdgeZ; // will be overridden per-point below
          }

          const bottomRingPoints = outerRing.map(([lng, lat]) => {
            const mc = maplibregl.MercatorCoordinate.fromLngLat([lng, lat], 0);
            const x = mc.x - originMerc.x;
            const y = mc.y - originMerc.y;

            let z = bottomZ;
            if (base === 0 && lowerCaps.length > 0) {
              // Per-point check for partial overlaps on non-stacked polygons
              for (const lower of lowerCaps) {
                try {
                  if (booleanPointInPolygon(point([lng, lat]), lower.feature)) {
                    const candidate = (lower.height + ZONE_TOP_CAP_EDGE_LIFT_METERS) * metersToMerc;
                    if (candidate > z) z = candidate;
                  }
                } catch (error) {
                  // skip on containment failure
                }
              }
            }

            return new THREE.Vector3(x, y, z);
          });

          this.addEdgeStack(
              scene,
              disposables,
              topRingPoints,
              bottomRingPoints,
              color,
              baseRenderOrder
          );

          // Side wall — quad strip connecting bottom ring to top ring
          const sidePositions = [];
          const ringLen = topRingPoints.length;
          for (let si = 0; si < ringLen; si++) {
            const si1 = (si + 1) % ringLen;
            const t0 = topRingPoints[si];
            const t1 = topRingPoints[si1];
            const b0 = bottomRingPoints[si];
            const b1 = bottomRingPoints[si1];
            sidePositions.push(t0.x, t0.y, t0.z, b0.x, b0.y, b0.z, b1.x, b1.y, b1.z);
            sidePositions.push(t0.x, t0.y, t0.z, b1.x, b1.y, b1.z, t1.x, t1.y, t1.z);
          }
          const sideGeometry = new THREE.BufferGeometry();
          sideGeometry.setAttribute('position', new THREE.Float32BufferAttribute(sidePositions, 3));
          disposables.push(sideGeometry);
          const sideMaterial = new THREE.MeshBasicMaterial({
            color: capBaseColor,
            transparent: true,
            opacity: 0.55,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false,
            blending: THREE.NormalBlending,
            polygonOffset: true,
            polygonOffsetFactor: 2,
            polygonOffsetUnits: 2
          });
          disposables.push(sideMaterial);
          const sideWall = new THREE.Mesh(sideGeometry, sideMaterial);
          sideWall.renderOrder = baseRenderOrder + 5;
          scene.add(sideWall);
        }
      }

      const ox = originMerc.x;
      const oy = originMerc.y;
      const oz = originMerc.z;

      const layerCamera = new THREE.Camera();
      let layerRenderer = null;
      const mapRef = this.map;
      let disposed = false;

      const disposeResources = () => {
        if (disposed) return;
        disposed = true;

        for (const resource of disposables) {
          try {
            resource?.dispose?.();
          } catch (error) {
            console.warn('Failed to dispose three.js resource:', error);
          }
        }

        if (layerRenderer) {
          try {
            layerRenderer.dispose();
          } catch (error) {
            console.warn('Failed to dispose three.js renderer:', error);
          }
          layerRenderer = null;
        }
      };

      const computeMetersPerPixel = () => {
        const center = mapRef.getCenter();
        const zoom = mapRef.getZoom();
        const latRad = (center.lat * Math.PI) / 180;
        return (
            (MAP_BASE_METERS_PER_PX * Math.abs(Math.cos(latRad))) /
            Math.pow(2, zoom)
        );
      };

      const threeLayer = {
        id: 'three-top-caps',
        type: 'custom',
        renderingMode: '3d',
        visible: true,
        disposeResources,

        onAdd(map, gl) {
          layerRenderer = new THREE.WebGLRenderer({
            canvas: map.getCanvas(),
            context: gl,
            antialias: true,
            alpha: true
          });
          layerRenderer.autoClear = false;
          layerRenderer.sortObjects = true;
        },

        render(_gl, args) {
          if (!this.visible || !layerRenderer || disposed) return;

          const rawMatrix =
              args?.defaultProjectionData?.projectionMatrix ??
              args?.defaultProjectionData?.mainMatrix ??
              args?.modelViewProjectionMatrix ??
              (Array.isArray(args) ? args : null);

          if (!rawMatrix) return;

          const mpp = computeMetersPerPixel();
          for (const m of dotMaterials) {
            m.uniforms.uMetersPerPixel.value = mpp;
          }

          const vpMatrix = new THREE.Matrix4().fromArray(rawMatrix);
          const modelMatrix = new THREE.Matrix4().makeTranslation(ox, oy, oz);
          layerCamera.projectionMatrix = vpMatrix.multiply(modelMatrix);

          layerRenderer.resetState();
          layerRenderer.render(scene, layerCamera);
          mapRef.triggerRepaint();
        },

        onRemove() {
          disposeResources();
        }
      };

      this.threeCapLayer = threeLayer;
      this.map.addLayer(threeLayer);
    },

    async addMapLayers(buildVersion = this.layerBuildVersion) {
      if (!this.map || buildVersion !== this.layerBuildVersion) return;

      const byProtocol = this.buildProtocolBuckets();
      this.clearProtocolMarkers();
      this.removeThreeCapLayer();

      const zoneDescriptors = [];

      for (const protocol of this.protocols) {
        const features = byProtocol[protocol.key] || [];
        if (!features.length) continue;

        const hullFeature = this.buildHullFeature(features);
        if (!hullFeature) continue;

        let footprintArea = 0;
        try {
          footprintArea = area(hullFeature);
        } catch (error) {
          footprintArea = 0;
        }

        zoneDescriptors.push({
          ...protocol,
          featureCount: features.length,
          features,
          hullFeature,
          footprintArea
        });
      }

      const orderedZones = zoneDescriptors.sort((a, b) => {
        if (b.footprintArea !== a.footprintArea) return b.footprintArea - a.footprintArea;
        return b.featureCount - a.featureCount;
      });

      const topCapItems = [];

      for (let stackIndex = 0; stackIndex < orderedZones.length; stackIndex += 1) {
        if (!this.map || buildVersion !== this.layerBuildVersion) return;

        const zone = orderedZones[stackIndex];
        const protocol = zone;
        const features = zone.features;
        const hullFeature = zone.hullFeature;
        const hullSourceId = `hull-src-${protocol.key}`;
        const pointSourceId = `mp-src-${protocol.key}`;

        const extrusionBase = this.computeStackedBase(hullFeature, topCapItems);
        const extrusionHeight = extrusionBase + ZONE_STACK_BASE_HEIGHT + stackIndex * ZONE_STACK_HEIGHT_STEP;

        this.safeAddSource(hullSourceId, {
          type: 'geojson',
          data: hullFeature
        });


        //real bottom line
        this.safeAddLayer({
          id: `hull-core-${protocol.key}`,
          type: 'line',
          source: hullSourceId,
          layout: { visibility: 'visible' },
          paint: {
            'line-color': '#ff0000',
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              8, 1.3,
              12, 1.8,
              16, 2.4
            ],
            'line-blur': 0.9,
            'line-opacity': 0.84
          }
        });
        topCapItems.push({
          key: protocol.key,
          color: protocol.color,
          base: extrusionBase,
          height: extrusionHeight,
          feature: hullFeature,
          stackIndex
        });

        const markerCoords = this.buildMarkerCoordinate(hullFeature);
        if (markerCoords) {
          const element = this.createProtocolMarkerElement(protocol);
          const marker = new maplibregl.Marker({
            element,
            anchor: 'bottom'
          })
              .setLngLat(markerCoords)
              .addTo(this.map);

          this.protocolMarkers.push(marker);
        }

        this.safeAddSource(pointSourceId, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features
          },
          cluster: true,
          clusterMaxZoom: 12,
          clusterRadius: 50
        });

        //cluster circle
        this.safeAddLayer({
          id: `mp-cluster-${protocol.key}`,
          type: 'circle',
          source: pointSourceId,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': protocol.color,
            'circle-radius': ['step', ['get', 'point_count'], 18, 10, 24, 50, 30],
            'circle-opacity': 0.82,
            'circle-stroke-width': 1.5,
            'circle-stroke-color': 'rgba(255,255,255,0.28)'
          }
        });

        this.safeAddLayer({
          id: `mp-count-${protocol.key}`,
          type: 'symbol',
          source: pointSourceId,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Noto Sans Regular'],
            'text-size': 11
          },
          paint: {
            'text-color': '#ffffff'
          }
        });

        this.safeAddLayer({
          id: `mp-point-${protocol.key}`,
          type: 'circle',
          source: pointSourceId,
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': protocol.color,
            'circle-radius': 7,
            'circle-stroke-width': 1.5,
            'circle-stroke-color': 'rgba(255,255,255,0.45)',
            'circle-opacity': 0.92
          }
        });

        this.bindPointInteractions(
            pointSourceId,
            `mp-cluster-${protocol.key}`,
            `mp-point-${protocol.key}`
        );
      }

      if (!this.map || buildVersion !== this.layerBuildVersion) return;

      if (topCapItems.length) {
        this.addCustomTopCapLayer(topCapItems);
      }
    },

    bindPointInteractions(sourceId, clusterId, pointId) {
      if (!this.map) return;

      if (!this.map.__dashboardHandlers) {
        this.map.__dashboardHandlers = new Set();
      }

      const pointClickKey = `click:${pointId}`;
      const clusterClickKey = `click:${clusterId}`;
      const enterPointKey = `mouseenter:${pointId}`;
      const leavePointKey = `mouseleave:${pointId}`;
      const enterClusterKey = `mouseenter:${clusterId}`;
      const leaveClusterKey = `mouseleave:${clusterId}`;

      if (!this.map.__dashboardHandlers.has(pointClickKey)) {
        this.map.on('click', pointId, event => {
          const feature = event?.features?.[0];
          if (!feature) return;

          const coords = [...feature.geometry.coordinates];
          const props = feature.properties || {};

          new maplibregl.Popup()
              .setLngLat(coords)
              .setHTML(`
              <div class="mpid-popup">
                <div class="popup-header">
                  <div class="popup-mpid-label">Metering Point</div>
                  <div class="popup-title">${props.mpid || 'N/A'}</div>
                </div>
                <div class="popup-section">
                  <div class="popup-row">
                    <span class="popup-label">Customer</span>
                    <span class="popup-value">${props.customer || 'N/A'}</span>
                  </div>
                  <div class="popup-row">
                    <span class="popup-label">Address</span>
                    <span class="popup-value">${props.street || 'N/A'}, ${props.city || 'N/A'}</span>
                  </div>
                  <div class="popup-row">
                    <span class="popup-label">Zip</span>
                    <span class="popup-value">${props.zipcode || 'N/A'}</span>
                  </div>
                </div>
              </div>
            `)
              .addTo(this.map);
        });

        this.map.__dashboardHandlers.add(pointClickKey);
      }

      if (!this.map.__dashboardHandlers.has(clusterClickKey)) {
        this.map.on('click', clusterId, event => {
          const feature = this.map.queryRenderedFeatures(event.point, { layers: [clusterId] })?.[0];
          const clusterIdValue = feature?.properties?.cluster_id;
          const source = this.map.getSource(sourceId);

          if (
              clusterIdValue == null ||
              !source ||
              typeof source.getClusterExpansionZoom !== 'function'
          ) {
            return;
          }

          source.getClusterExpansionZoom(clusterIdValue, (error, zoom) => {
            if (error) return;
            this.map.easeTo({
              center: feature.geometry.coordinates,
              zoom
            });
          });
        });

        this.map.__dashboardHandlers.add(clusterClickKey);
      }

      if (!this.map.__dashboardHandlers.has(enterPointKey)) {
        this.map.on('mouseenter', pointId, () => {
          this.map.getCanvas().style.cursor = 'pointer';
        });
        this.map.__dashboardHandlers.add(enterPointKey);
      }

      if (!this.map.__dashboardHandlers.has(leavePointKey)) {
        this.map.on('mouseleave', pointId, () => {
          this.map.getCanvas().style.cursor = '';
        });
        this.map.__dashboardHandlers.add(leavePointKey);
      }

      if (!this.map.__dashboardHandlers.has(enterClusterKey)) {
        this.map.on('mouseenter', clusterId, () => {
          this.map.getCanvas().style.cursor = 'pointer';
        });
        this.map.__dashboardHandlers.add(enterClusterKey);
      }

      if (!this.map.__dashboardHandlers.has(leaveClusterKey)) {
        this.map.on('mouseleave', clusterId, () => {
          this.map.getCanvas().style.cursor = '';
        });
        this.map.__dashboardHandlers.add(leaveClusterKey);
      }
    },

    fitMapToPoints() {
      if (!this.map || !this.meteringPoints.length) return;

      const bounds = new maplibregl.LngLatBounds();

      for (const mp of this.meteringPoints) {
        const lon = parseFloat(mp?.lon);
        const lat = parseFloat(mp?.lat);

        if (Number.isFinite(lon) && Number.isFinite(lat)) {
          bounds.extend([lon, lat]);
        }
      }

      if (bounds.isEmpty()) return;

      this.map.fitBounds(bounds, {
        padding: MAP_PADDING,
        minZoom: 11,
        maxZoom: 14,
        duration: 1400
      });
    },

    applyMapMode(isLow) {
      if (!this.map) return;

      for (const protocol of this.protocols) {
        [
          `hull-soft-fill-${protocol.key}`,
          `hull-core-${protocol.key}`,
          `hull-edge-${protocol.key}`
        ].forEach(id => this.safeSetVisibility(id, isLow));

        [
          `mp-cluster-${protocol.key}`,
          `mp-count-${protocol.key}`,
          `mp-point-${protocol.key}`
        ].forEach(id => this.safeSetVisibility(id, !isLow));
      }

      this.toggleProtocolMarkers(isLow);

      if (this.threeCapLayer) {
        this.threeCapLayer.visible = isLow;
        this.map.triggerRepaint();
      }
    },

    setMapMode(enable3d) {
      this.show3d = enable3d;
      this.applyMapMode(enable3d);
    },

    initZoomWatcher() {
      if (!this.map) return;

      if (this.zoomWatcher) {
        this.map.off('zoomend', this.zoomWatcher);
      }

      // Apply initial mode from toggle
      this.applyMapMode(this.show3d);

      this.zoomWatcher = null;
    },

    removeDynamicMapLayers() {
      if (!this.map) return;

      this.removeThreeCapLayer();
      this.clearProtocolMarkers();
      this.clearAlarmMarkers();

      for (const protocol of this.protocols) {
        [
          `hull-edge-${protocol.key}`,
          `hull-core-${protocol.key}`,
          `hull-soft-fill-${protocol.key}`,
          `hull-extrusion-${protocol.key}`,
          `mp-count-${protocol.key}`,
          `mp-cluster-${protocol.key}`,
          `mp-point-${protocol.key}`
        ].forEach(id => this.safeRemoveLayer(id));

        [
          `hull-src-${protocol.key}`,
          `mp-src-${protocol.key}`
        ].forEach(id => this.safeRemoveSource(id));
      }
    },

    barGradient(color) {
      return [
        `linear-gradient(to bottom, ${color}dd 0px, ${color}00 15px)`,
        `repeating-linear-gradient(
          to bottom,
          ${color}99 0px,
          ${color}99 2.5px,
          ${color}20 2.5px,
          ${color}20 6px
        )`
      ].join(', ');
    },

    cleanup() {
      if (!this.map) return;

      this.layerBuildVersion += 1;
      this.mapIsReady = false;

      try {
        if (this.zoomWatcher) {
          this.map.off('zoomend', this.zoomWatcher);
          this.zoomWatcher = null;
        }
        this.detachAlarmClusterMoveWatcher();

        this.removeDynamicMapLayers();
        this.map.remove();
      } catch (error) {
        console.warn('Map cleanup failed:', error);
      } finally {
        this.map = null;
      }
    }
  }
};
</script>

<style scoped>
.dashboard-page {
  min-height: 100vh;
  color: #fff;
  font-family: 'Inter', sans-serif;
  position: relative;
  overflow: hidden;
}

.map-container {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.map-overlay-red-50 {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(to bottom, rgba(5, 8, 16, 0.10), rgba(2, 4, 10, 0.48));
  mix-blend-mode: screen;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.loading-container {
  min-width: 300px;
  padding: 40px;
  text-align: center;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.loading-spinner {
  width: 50px;
  height: 50px;
  margin: 0 auto 20px;
  border-radius: 50%;
  border: 4px solid rgba(79, 195, 247, 0.2);
  border-top: 4px solid #4fc3f7;
  animation: spin 1s linear infinite;
}

.loading-text {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.error-overlay {
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
}

.error-container {
  min-width: 350px;
  padding: 30px;
  text-align: center;
  border-radius: 12px;
  background: rgba(239, 68, 68, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.error-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.error-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #fff;
}

.error-message {
  font-size: 14px;
  margin-bottom: 20px;
  color: #fff;
  opacity: 0.9;
}

.retry-button {
  border: 1px solid #fff;
  color: #fff;
  cursor: pointer;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.18);
  transition: all 0.2s ease;
}

.retry-button:hover {
  background: rgba(255, 255, 255, 0.30);
}

:deep(.maplibregl-popup) {
  font-family: 'Inter', sans-serif;
}

:deep(.maplibregl-popup-content) {
  min-width: 240px;
  padding: 0 !important;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.96);
  border-radius: 18px !important;
  background: rgba(21, 21, 21, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-top: 1px solid rgba(255, 255, 255, 0.18) !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255,255,255,0.08) !important;
  backdrop-filter: blur(30px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
}

:deep(.maplibregl-popup-tip) {
  border-top-color: rgba(21, 21, 21, 0.16) !important;
}

:deep(.maplibregl-popup-close-button) {
  top: 4px;
  right: 4px;
  color: rgba(255, 255, 255, 0.28);
  font-size: 15px;
  padding: 6px 9px;
  line-height: 1;
  transition: color 0.16s;
}

:deep(.maplibregl-popup-close-button:hover) {
  color: rgba(255, 255, 255, 0.8);
  background: transparent;
}

:deep(.mpid-popup) {
  padding: 0;
}

:deep(.popup-header) {
  padding: 16px 16px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

:deep(.popup-mpid-label) {
  margin-bottom: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.44);
}

:deep(.popup-title) {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.01em;
  color: rgba(255, 255, 255, 0.96);
}

:deep(.popup-section) {
  padding: 12px 16px 14px;
}

:deep(.popup-row) {
  display: flex;
  gap: 16px;
  font-size: 12px;
  margin-bottom: 6px;
  font-weight: 500;
  line-height: 16px;
  align-items: baseline;
  justify-content: space-between;
}

:deep(.popup-row:last-child) {
  margin-bottom: 0;
}

:deep(.popup-label) {
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.44);
}

:deep(.popup-value) {
  text-align: right;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.68);
}

:deep(.popup-section-title) {
  margin: 10px 0 7px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 157, 77, 0.66);
}

:deep(.alarm-duration-row .popup-value) {
  color: rgba(255, 255, 255, 0.84);
}

:deep(.persistent-alarm-row) {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 7px;
  padding: 8px 9px;
  border-radius: 8px;
  background: rgba(255, 157, 77, 0.08);
  border: 1px solid rgba(255, 157, 77, 0.14);
}

:deep(.persistent-alarm-row:last-child) {
  margin-bottom: 0;
}

:deep(.persistent-alarm-row span) {
  min-width: 0;
  color: rgba(255, 255, 255, 0.82);
  font-size: 11px;
  font-weight: 700;
}

:deep(.persistent-alarm-row strong) {
  flex-shrink: 0;
  max-width: 128px;
  color: rgba(255, 157, 77, 0.92);
  font-size: 10px;
  font-weight: 750;
  text-align: right;
}

.widget-rail {
  position: fixed;
  top: 72px;
  z-index: 100;
  width: 300px;
  gap: 20px;
  display: flex;
  pointer-events: none;
  flex-direction: column;
}

.widget-rail > * {
  pointer-events: all;
}

.widget-rail-left {
  left: 50px;
}

.widget-rail-right {
  right: 50px;
}

.widget {
  overflow: hidden;
  border-radius: 18px;
  color: rgba(255, 255, 255, 0.96);
  background: rgba(21, 21, 21, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.38), inset 0 1px 0 rgba(255,255,255,0.06);
  backdrop-filter: blur(30px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
}

.widget-alert {
  border-left: 2px solid rgba(255, 157, 77, 0.5);
}

.widget-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 14px;
}

.widget-title {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.96);
  letter-spacing: 0.01em;
}

.widget-kpi {
  display: flex;
  gap: 6px;
  padding: 8px 16px 12px;
  align-items: baseline;
}

.kpi-value {
  font-size: 32px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.96);
}

.kpi-value.kpi-warn {
  color: #ff9d4d;
}

.kpi-unit {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.44);
}

.widget-content {
  padding: 0 16px 14px;
}

.widget-footer {
  padding: 8px 16px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.footer-note {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.28);
}

.fleet-chart {
  padding: 0 14px 14px;
}

.fleet-chart-inner {
  position: relative;
  height: 110px;
  padding-left: 28px;
  padding-bottom: 20px;
}

.fleet-grid {
  position: absolute;
  inset: 0;
  padding-left: 28px;
  padding-bottom: 20px;
  pointer-events: none;
}

.fleet-gridline {
  position: absolute;
  left: 28px;
  right: 0;
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
}

.fleet-grid-label {
  position: absolute;
  top: -7px;
  left: -28px;
  width: 24px;
  font-size: 10px;
  font-weight: 500;
  text-align: right;
  color: rgba(255, 255, 255, 0.28);
}

.fleet-bars {
  display: flex;
  gap: 10px;
  height: 100%;
  align-items: flex-end;
}

.fleet-bar-col {
  flex: 1;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-direction: column;
}

.fleet-bar {
  width: 100%;
  min-height: 3px;
  border-radius: 4px 4px 0 0;
  transition: height 0.4s ease;
}

.fleet-bar-label {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 5px;
  text-align: center;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.38);
}

.meta-row {
  display: flex;
  font-size: 12px;
  margin-bottom: 8px;
  align-items: center;
  justify-content: space-between;
}

.meta-label {
  color: rgba(255, 255, 255, 0.44);
}

.meta-value {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.68);
}

.meta-value.meta-warn {
  color: #ff9d4d;
}

.meta-bar-wrap {
  gap: 2px;
  height: 5px;
  display: flex;
  overflow: hidden;
  border-radius: 999px;
  margin-bottom: 8px;
}

.meta-bar {
  height: 100%;
  border-radius: 999px;
}

.meta-bar-fill {
  opacity: 0.8;
  background: #5bb6ff;
}

.meta-bar-warn {
  opacity: 0.8;
  background: #ff9d4d;
}

.meta-legend {
  gap: 4px;
  display: flex;
  font-size: 10px;
  align-items: center;
  color: rgba(255, 255, 255, 0.4);
}

.legend-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-label {
  font-weight: 500;
}

.stat-tiles {
  display: flex;
  gap: 8px;
}

.stat-tile {
  flex: 1;
  padding: 12px 10px;
  text-align: center;
  border-radius: 14px;
  background: rgba(10, 14, 22, 0.44);
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
}

.stat-value {
  font-size: 20px;
  line-height: 1.1;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.96);
}

.stat-value.stat-warn {
  color: #ff9d4d;
}

.stat-label {
  margin-top: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
}

/* Map style switcher — compact glass cards */
.map-mode-toggle {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding: 0 14px 14px;
}

.map-style-toggle {
  padding-top: 0;
}

.map-mode-btn {
  min-width: 0;
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 50px;
  padding: 9px 10px;
  border-radius: 11px;
  border: 11px solid hsl(0deg 0% 100% / 0%);
  background: rgba(255, 255, 255, 0.038);
  color: rgba(255, 255, 255, 0.43);
  cursor: pointer;
  transition: color 0.18s ease, background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.map-mode-btn:hover {
  background: rgba(255, 255, 255, 0.065);
  color: rgba(255, 255, 255, 0.74);
  border-color: rgba(255, 255, 255, 0.13);
}

.map-mode-btn.active {
  color: rgb(204 204 204 / 0.98);
  background: linear-gradient(180deg, rgb(91 182 255 / 0%), rgba(91, 182, 255, .08));
  border: 1px solid hsl(0deg 0% 100% / 6%);
}

.map-mode-icon {
  width: 17px;
  height: 17px;
  flex-shrink: 0;
}

.map-mode-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10.5px;
  font-weight: 650;
  letter-spacing: 0.01em;
  text-align: left;
}

.alarm-selection-toggle {
  padding: 8px 0 0;
}

.alarm-selection-toggle .map-mode-btn {
  min-height: 46px;
  padding: 8px 9px;
}

.alarm-selection-toggle .map-mode-label {
  white-space: normal;
  line-height: 1.15;
}

.alarm-selection-toggle .alarm-persistent-toggle {
  grid-column: 1 / -1;
}

.tab-group {
  gap: 2px;
  display: flex;
  padding: 2px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
}

.tab {
  border: none;
  cursor: pointer;
  font-size: 10px;
  padding: 3px 9px;
  border-radius: 999px;
  background: transparent;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  letter-spacing: 0.03em;
  color: rgba(255, 255, 255, 0.4);
  transition: all 0.16s;
}

.tab.active {
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.9);
}

.tab:hover:not(.active) {
  color: rgba(255, 255, 255, 0.65);
}

.comm-row {
  gap: 7px;
  display: flex;
  margin-bottom: 8px;
  align-items: center;
}

.comm-row:last-child {
  margin-bottom: 0;
}

.comm-dot {
  width: 6px;
  height: 6px;
  opacity: 0.85;
  border-radius: 50%;
  flex-shrink: 0;
}

.comm-name {
  width: 68px;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.44);
}

.comm-bar-wrap {
  flex: 1;
  height: 4px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
}

.comm-bar {
  height: 100%;
  opacity: 0.8;
  border-radius: 999px;
  transition: width 0.4s ease;
}

.comm-fraction {
  white-space: nowrap;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.68);
}

.comm-total {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.28);
}

.status-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.badge-warn {
  color: #ff9d4d;
  background: rgba(255, 157, 77, 0.15);
  border: 1px solid rgba(255, 157, 77, 0.3);
}

.badge-danger {
  color: #ff5b6e;
  background: rgba(255, 91, 110, 0.15);
  border: 1px solid rgba(255, 91, 110, 0.32);
}

.kpi-value.kpi-danger,
.stat-value.stat-danger {
  color: #ff5b6e;
}

.alarm-type-list,
.latest-alarm-list {
  display: flex;
  gap: 8px;
  flex-direction: column;
}

.alarm-empty {
  padding: 8px 0;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.42);
}

.alarm-type-row,
.latest-alarm-row {
  display: flex;
  align-items: center;
  min-height: 34px;
  gap: 8px;
  padding: 8px 0;
}

.alarm-type-row:first-child,
.latest-alarm-row:first-child {
  border-top: none;
}

.alarm-type-row {
  cursor: pointer;
  border-radius: 8px;
  padding-inline: 8px;
  outline: none;
  transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease;
}

.alarm-type-row:hover,
.alarm-type-row:focus,
.alarm-type-row.active {
  background: rgba(255, 91, 110, 0.10);
  border-top-color: rgba(255, 91, 110, 0.16);
}

.alarm-type-row.active .alarm-type-name,
.alarm-type-row.active .alarm-type-count {
  color: rgba(255, 255, 255, 0.96);
}

.alarm-type-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex-shrink: 0;
}

.alarm-type-name {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 650;
  color: rgba(255, 255, 255, 0.72);
}

.alarm-type-count {
  font-size: 12px;
  font-weight: 750;
  color: rgba(255, 255, 255, 0.94);
}

.latest-alarm-row {
  justify-content: space-between;
}

.latest-alarm-ldn {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 750;
  color: rgba(255, 255, 255, 0.86);
}

.latest-alarm-meta {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 2px;
  font-size: 10px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.42);
}

.latest-alarm-time {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 650;
  color: rgba(255, 255, 255, 0.38);
}

.alarm-action-button {
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.10);
  min-height: 34px;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.045);
  color: rgba(255, 255, 255, 0.62);
  font-size: 11px;
  font-weight: 700;
  font-family: 'Inter', sans-serif;
  transition: all 0.16s ease;
}

.alarm-action-button:hover,
.alarm-action-button.active {
  color: rgba(255, 255, 255, 0.94);
  background: rgba(255, 91, 110, 0.12);
  border-color: rgba(255, 91, 110, 0.32);
}

.proto-tag {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 999px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5bb6ff;
  background: rgba(91, 182, 255, 0.12);
  border: 1px solid rgba(91, 182, 255, 0.25);
  white-space: nowrap;
}

.sim-gauge-body {
  gap: 4px;
  display: flex;
  padding: 8px 12px 14px 8px;
  align-items: center;
}

.sim-gauge-chart {
  flex-shrink: 0;
}

.sim-gauge-info {
  gap: 8px;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.sim-status-row {
  gap: 6px;
  display: flex;
  align-items: center;
}

.sim-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.sim-status-text {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.sim-num-group {
  gap: 10px;
  display: flex;
  align-items: center;
}

.sim-num-item {
  gap: 1px;
  display: flex;
  flex-direction: column;
}

.sim-num-val {
  font-size: 16px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: rgba(255, 255, 255, 0.9);
}

.sim-num-lbl {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.03em;
  color: rgba(255, 255, 255, 0.38);
}

.sim-num-divider {
  width: 1px;
  height: 24px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.08);
}

.sim-gauge-desc {
  margin: 0;
  font-size: 11px;
  line-height: 1.5;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.44);
}

:deep(.protocol-callout-marker) {
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translateY(-2px);
}

:deep(.protocol-callout-pill) {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  color: rgba(255,255,255,0.92);
  background: rgba(30, 33, 46, 0.76);
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(14px) saturate(135%);
  -webkit-backdrop-filter: blur(14px) saturate(135%);
}

:deep(.protocol-callout-text) {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

:deep(.protocol-callout-icon) {
  width: 8px;
  height: 8px;
  border-radius: 999px;
}

:deep(.protocol-callout-stem) {
  width: 1px;
  height: 38px;
  opacity: 0.92;
}

:deep(.protocol-callout-anchor-wrap) {
  position: relative;
  width: 16px;
  height: 16px;
}

:deep(.protocol-callout-anchor-glow) {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  filter: blur(6px);
}

:deep(.protocol-callout-anchor) {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 8px;
  height: 8px;
  border-radius: 999px;
}

:deep(.alarm-callout-pill) {
  background: rgba(21, 21, 21, 0.16);
  border-color: rgba(255, 255, 255, 0.10);
  box-shadow: none;
  backdrop-filter: blur(30px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
}

:deep(.alarm-callout-marker) {
  cursor: pointer;
  pointer-events: auto;
}

:deep(.alarm-callout-text) {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.alarm-cluster-pill) {
  gap: 7px;
  padding-left: 8px;
}

:deep(.alarm-cluster-count) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  color: rgba(10, 14, 22, 0.92);
  font-size: 11px;
  font-weight: 850;
  line-height: 1;
  box-shadow: none;
}

:deep(.alarm-popup .popup-title) {
  font-size: 18px;
}

:deep(.popup-device-link) {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  margin: 0 14px 14px;
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.94);
  background: rgba(255, 91, 110, 0.16);
  border: 1px solid rgba(255, 91, 110, 0.32);
  font-size: 12px;
  font-weight: 750;
  text-decoration: none;
}

:deep(.popup-device-link:hover) {
  background: rgba(255, 91, 110, 0.24);
}

:deep(.alarm-cluster-popup-list) {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 260px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.30) rgba(255, 255, 255, 0.04);
}

:deep(.alarm-cluster-popup-list::-webkit-scrollbar) {
  width: 8px;
}

:deep(.alarm-cluster-popup-list::-webkit-scrollbar-track) {
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
}

:deep(.alarm-cluster-popup-list::-webkit-scrollbar-thumb) {
  border: 2px solid rgba(21, 21, 21, 0.16);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.30);
}

:deep(.alarm-cluster-popup-list::-webkit-scrollbar-thumb:hover) {
  background: rgba(255, 255, 255, 0.46);
}

:deep(.alarm-cluster-persistent-summary) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 9px;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.58);
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.07);
  font-size: 10px;
  font-weight: 750;
  text-transform: uppercase;
}

:deep(.alarm-cluster-persistent-summary.has-persistent) {
  color: rgba(255, 157, 77, 0.92);
  background: rgba(255, 157, 77, 0.09);
  border-color: rgba(255, 157, 77, 0.2);
}

:deep(.alarm-cluster-persistent-summary strong) {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 10px;
  font-weight: 800;
  text-transform: none;
}

:deep(.alarm-cluster-popup-row) {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 8px 9px;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.74);
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid transparent;
  text-decoration: none;
  font-size: 11px;
  font-weight: 650;
}

:deep(.alarm-cluster-popup-row.has-persistent) {
  background: rgba(255, 157, 77, 0.08);
  border: 1px solid rgba(255, 157, 77, 0.14);
}

:deep(.alarm-cluster-popup-row:hover) {
  color: rgba(255, 255, 255, 0.96);
  background: rgba(255, 91, 110, 0.13);
}

:deep(.alarm-cluster-device-cell) {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

:deep(.alarm-cluster-device-main) {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 6px;
}

:deep(.alarm-cluster-ldn) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.alarm-cluster-device-cell small) {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(255, 157, 77, 0.82);
  font-size: 10px;
  font-weight: 700;
}

:deep(.alarm-cluster-persistent-badge) {
  flex-shrink: 0;
  padding: 2px 5px;
  border-radius: 999px;
  color: rgba(10, 14, 22, 0.92);
  background: rgba(255, 157, 77, 0.96);
  font-size: 8px;
  font-weight: 850;
  line-height: 1;
  text-transform: uppercase;
}

:deep(.alarm-cluster-popup-row strong) {
  flex-shrink: 0;
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(255, 157, 77, 0.92);
  font-size: 10px;
  font-weight: 750;
}

:deep(.alarm-cluster-popup-more) {
  padding: 4px 2px 0;
  color: rgba(255, 255, 255, 0.38);
  font-size: 11px;
  font-weight: 650;
  text-align: center;
}

.alarm-history-widget {
  position: fixed;
  left: 50%;
  bottom: 28px;
  z-index: 110;
  width: min(760px, calc(100vw - 760px));
  min-width: 460px;
  transform: translateX(-50%);
  pointer-events: all;
}

.alarm-history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 14px 16px 10px;
}

.alarm-history-header .alarm-action-button {
  width: auto;
  min-width: 86px;
}

.alarm-history-subtitle {
  display: block;
  margin-top: 3px;
  font-size: 10px;
  font-weight: 550;
  color: rgba(255, 255, 255, 0.34);
}

.alarm-history-chart-wrap {
  position: relative;
  height: 132px;
  margin: 0 14px 14px;
  overflow: hidden;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.07);
}

.alarm-history-chart-wrap.loading::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 91, 110, 0.10), transparent);
  animation: alarmHistorySweep 1.35s ease-in-out infinite;
}

.alarm-history-chart-wrap:not(.loading)::after {
  content: none;
  animation: none;
}

@keyframes alarmHistorySweep {
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
}

.alarm-history-chart {
  position: absolute;
  inset: 12px 14px 28px;
  width: calc(100% - 28px);
  height: calc(100% - 40px);
  overflow: visible;
}

.alarm-history-guide {
  stroke: rgba(255, 255, 255, 0.08);
  stroke-width: 0.55;
}

.alarm-history-area {
  fill: url(#alarmHistoryAreaGradient);
}

.alarm-history-line {
  fill: none;
  stroke: url(#alarmHistoryLineGradient);
  stroke-width: 1.8;
  stroke-linecap: round;
}

.alarm-history-hit {
  cursor: pointer;
  outline: none;
}

.alarm-history-click-zone {
  fill: transparent;
}

.alarm-history-hit:hover .alarm-history-click-zone,
.alarm-history-hit:focus .alarm-history-click-zone {
  fill: rgba(255, 91, 110, 0.08);
}

.alarm-history-selected-line {
  stroke: rgba(255, 255, 255, 0.36);
  stroke-width: 0.65;
  stroke-dasharray: 2 2;
}

.alarm-history-points-layer {
  position: absolute;
  inset: 12px 14px 28px;
  pointer-events: none;
}

.alarm-history-point-dot {
  position: absolute;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  transform: translate(-50%, -50%);
  background: #ff9d4d;
  border: 1px solid rgba(255, 255, 255, 0.55);
  box-shadow: 0 0 12px rgba(255, 91, 110, 0.42);
}

.alarm-history-point-dot.active {
  width: 10px;
  height: 10px;
  background: #ff5b6e;
  box-shadow: 0 0 18px rgba(255, 91, 110, 0.68);
}

.alarm-history-label-row {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 8px;
  display: grid;
  grid-template-columns: repeat(14, minmax(0, 1fr));
  gap: 2px;
}

.alarm-history-label-button {
  min-width: 0;
  border: none;
  cursor: pointer;
  padding: 3px 1px;
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.32);
  font-size: 9px;
  font-weight: 650;
  font-family: 'Inter', sans-serif;
}

.alarm-history-label-button:hover,
.alarm-history-label-button.active {
  color: rgba(255, 255, 255, 0.88);
  background: rgba(255, 91, 110, 0.12);
}

.alarm-history-panel-enter-active,
.alarm-history-panel-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.alarm-history-panel-enter-from,
.alarm-history-panel-leave-to {
  opacity: 0;
  transform: translate(-50%, 10px);
}

:deep(.maplibregl-ctrl) {
  z-index: 10;
}

:deep(.maplibregl-canvas) {
  outline: none;
}

@media (max-width: 1440px) {
  .widget-rail-left {
    left: 24px;
  }

  .widget-rail-right {
    right: 24px;
  }

  .widget-rail {
    width: 280px;
  }
}

@media (max-width: 1180px) {
  .widget-rail {
    width: 240px;
    gap: 14px;
  }

  .widget-rail-left,
  .widget-rail-right {
    top: 76px;
  }

  .kpi-value {
    font-size: 28px;
  }
}

@media (max-width: 980px) {
  .widget-rail {
    position: absolute;
    width: calc(50% - 28px);
    top: 84px;
  }

  .widget-rail-left {
    left: 16px;
  }

  .widget-rail-right {
    right: 16px;
  }

  .alarm-history-widget {
    left: 16px;
    right: 16px;
    bottom: 16px;
    width: auto;
    min-width: 0;
    transform: none;
  }

  .alarm-history-panel-enter-from,
  .alarm-history-panel-leave-to {
    transform: translateY(10px);
  }
}

@media (max-width: 768px) {
  .widget-rail {
    position: static;
    width: auto;
    padding: 88px 16px 16px;
  }

  .widget-rail-left,
  .widget-rail-right {
    left: auto;
    right: auto;
    top: auto;
  }

  .dashboard-page {
    overflow-y: auto;
  }

  .stat-tiles {
    flex-direction: column;
  }

  .widget {
    border-radius: 16px;
  }

  .alarm-history-widget {
    position: relative;
    left: auto;
    right: auto;
    bottom: auto;
    margin: 0 16px 16px;
  }
}

.latest-alarm-ldn-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: rgba(255, 255, 255, 0.94);
  text-decoration: none;
  transition:
      color 0.18s ease,
      opacity 0.18s ease,
      transform 0.18s ease;
}

.latest-alarm-ldn-link:hover {
  color: #5bb6ff;
  opacity: 1;
  transform: translateX(1px);
}

.latest-alarm-link-icon {
  font-size: 10px;
  line-height: 1;
  opacity: 0.62;
}
</style>
